import { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import {
  X,
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  CalendarDays,
  CalendarClock,
  UploadCloud,
  Sparkles,
  Globe,
  ArrowLeft,
  Info,
  FileText,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { toast } from "../components/ui/sonner";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../components/ui/command";
import { COUNTRIES, CURRENCIES } from "../lib/worldData";
import {
  PaymentTrackingSection,
  CustomCostsSection,
  RentalTrackingSection,
  GlobalDocumentsSection,
  serializePayments,
  serializeCustomCosts,
  customCostsBaseTotal,
  rentalEngineInputs,
  normalizeRental,
} from "./SimpleGlobalFinancialSections";

const currencySymbols = { AED: "AED", GBP: "£", EUR: "€", TRY: "₺" };

// Country-specific realistic placeholders so investors see the right "shape" of numbers.
const PLACEHOLDERS = {
  dubai: {
    name: "Marina Bay Tower · Apt 2103",
    price: "2850000",
    area: "1450",
    rent: "12500",
    service_fee_per_sqft: "67.54",
    maintenance_per_sqft: "15",
  },
  north_cyprus: {
    name: "Caesar Resort · Apt B-204",
    price: "165000",
    area: "120",
    rent: "850",
    site_fee_per_sqm: "8.14",
    property_tax_annual: "120",
    transformer: "2000",
    solicitor: "2500",
  },
  germany: {
    name: "Münchner Freiheit · Wohnung 12",
    price: "350000",
    area: "78",
    rent: "1450",
    hausgeld_umlage: "180",
    hausgeld_nicht_umlage: "80",
    ruecklage: "50",
  },
};

const BUNDESLAENDER = [
  { code: "bayern", label: "Bayern (3.5%)" },
  { code: "sachsen", label: "Sachsen (3.5%)" },
  { code: "hamburg", label: "Hamburg (5.5%)" },
  { code: "baden_wuerttemberg", label: "Baden-Württemberg (5.0%)" },
  { code: "bremen", label: "Bremen (5.0%)" },
  { code: "niedersachsen", label: "Niedersachsen (5.0%)" },
  { code: "rheinland_pfalz", label: "Rheinland-Pfalz (5.0%)" },
  { code: "sachsen_anhalt", label: "Sachsen-Anhalt (5.0%)" },
  { code: "mecklenburg_vorpommern", label: "Mecklenburg-Vorpommern (6.0%)" },
  { code: "berlin", label: "Berlin (6.0%)" },
  { code: "hessen", label: "Hessen (6.0%)" },
  { code: "brandenburg", label: "Brandenburg (6.5%)" },
  { code: "nrw", label: "Nordrhein-Westfalen (6.5%)" },
  { code: "saarland", label: "Saarland (6.5%)" },
  { code: "schleswig_holstein", label: "Schleswig-Holstein (6.5%)" },
  { code: "thueringen", label: "Thüringen (6.5%)" },
];

const STEPS = [
  { n: 1, label: "Property" },
  { n: 2, label: "Acquisition" },
  { n: 3, label: "Operations" },
  { n: 4, label: "Financing" },
  { n: 5, label: "Review" },
];

// ---------------------------------------------------------------------------
// AutoCalcRow: shows a backend-computed value with an [Override] affordance.
// ---------------------------------------------------------------------------
function AutoCalcRow({
  label,
  hint,
  computed,
  value,
  onChange,
  sym,
  isOverriding,
  onStartOverride,
  onResetOverride,
  testId,
  disabled = false,
}) {
  const fmt = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? `${sym} ${Math.round(Number(n)).toLocaleString()}`
      : `${sym} —`;

  return (
    <div className="flex justify-between items-center text-xs gap-3">
      <div className="flex-1 min-w-0">
        <span className="text-white/80">{label}</span>
        {hint && (
          <span className="block text-white/60 text-[9px] mt-0.5">{hint}</span>
        )}
      </div>
      {!isOverriding ? (
        <div className="flex items-center gap-3 shrink-0">
          <span
            className={`font-mono text-xs ${disabled ? "text-white/60" : "text-white/80"}`}
            data-testid={`${testId}-computed`}
          >
            {disabled ? `${sym} —` : fmt(computed)}
          </span>
          {!disabled && (
            <button
              type="button"
              data-testid={`${testId}-override-btn`}
              onClick={onStartOverride}
              className="text-[9px] tracking-[0.12em] uppercase text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors"
            >
              Override
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 shrink-0">
          <input
            data-testid={testId}
            type="number"
            step="1"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoFocus
            className="w-24 sm:w-28 bg-[#050505] border border-[#D4AF37]/40 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
            placeholder={
              computed != null && !Number.isNaN(Number(computed))
                ? Math.round(Number(computed)).toLocaleString()
                : "0"
            }
          />
          <span className="text-white/60 text-[9px]">{sym}</span>
          <button
            type="button"
            data-testid={`${testId}-reset-btn`}
            onClick={() => {
              onChange("");
              onResetOverride();
            }}
            className="text-[9px] tracking-[0.12em] uppercase text-white/60 hover:text-white transition-colors"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeferredPill: replaces an optional input with a friendly "set later" pill.
// ---------------------------------------------------------------------------
function DeferredPill({ label, onAddNow, testId }) {
  return (
    <div
      data-testid={`${testId}-pill`}
      className="flex items-center justify-between bg-white/[0.02] border border-dashed border-white/15 px-3 py-2.5"
    >
      <div className="flex items-center gap-2">
        <CalendarClock className="w-3.5 h-3.5 text-white/60" />
        <span className="text-xs text-white/80">{label}</span>
      </div>
      <button
        type="button"
        data-testid={`${testId}-add-now`}
        onClick={onAddNow}
        className="text-[10px] tracking-[0.12em] uppercase text-[#D4AF37]/80 hover:text-[#D4AF37] transition-colors"
      >
        Add now
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 layout primitives (operations).
// SectionLabel: small caps section heading.
// InlineRow: borderless row with a left label and a right-aligned slim input.
// ---------------------------------------------------------------------------
function SectionLabel({ children, testId }) {
  return (
    <p
      data-testid={testId}
      style={{
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "#555",
        marginBottom: "14px",
      }}
    >
      {children}
    </p>
  );
}

function InlineRow({
  label,
  hint,
  children,
  isLast = false,
}) {
  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5"
      style={{ borderBottom: isLast ? "none" : "0.5px solid #181818" }}
    >
      <div className="min-w-0 flex-1">
        <span style={{ fontSize: "13px", color: "#FFFFFF" }}>{label}</span>
        {hint && (
          <span className="block text-[10px] text-white/45 mt-0.5">{hint}</span>
        )}
      </div>
      <div className="shrink-0" style={{ width: "140px" }}>
        {children}
      </div>
    </div>
  );
}

// Input variant used by every inline row in Sections 2 and 4. Visually
// minimal — transparent background, single bottom border that aligns with
// the row's own bottom border so the input feels like part of the row.
const inlineInputCls =
  "w-full bg-transparent text-white text-right font-mono text-[13px] py-1 outline-none focus:outline-none transition-colors";
const inlineInputStyle = { borderBottom: "0.5px solid #2a2a2a" };

// ---------------------------------------------------------------------------
// WizardStepper: horizontal progress bar with 4 step pills.
// State per step: DONE, ACTIVE, UPCOMING.
// ---------------------------------------------------------------------------
function WizardStepper({ current }) {
  const total = STEPS.length;
  const activeStep = STEPS.find((s) => s.n === current) || STEPS[0];
  return (
    <div
      className="px-5 pt-4 pb-3 border-b border-white/[0.10] bg-[#0A0A0A]"
      data-testid="wizard-stepper"
    >
      {/* Dots-only bar */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, idx) => {
          const state =
            s.n < current ? "done" : s.n === current ? "active" : "upcoming";
          return (
            <div
              key={s.n}
              className="flex items-center"
              style={{ flex: idx < STEPS.length - 1 ? 1 : "0 0 auto" }}
            >
              <span
                data-testid={`wizard-step-${s.n}`}
                data-step-state={state}
                className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[10px] font-mono transition-colors ${
                  state === "done"
                    ? "bg-[#D4AF37] text-black"
                    : state === "active"
                    ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]"
                    : "bg-white/[0.04] border border-white/[0.12]"
                }`}
                style={state === "upcoming" ? { color: "#555" } : undefined}
              >
                {state === "done" ? <Check className="w-3 h-3" /> : s.n}
              </span>
              {idx < STEPS.length - 1 && (
                <span
                  className={`mx-2 sm:mx-3 h-px flex-1 ${
                    s.n < current ? "bg-[#D4AF37]/40" : "bg-white/[0.08]"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Single status line below the bar */}
      <p
        data-testid="wizard-step-status"
        className="mt-3"
        style={{
          fontSize: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        <span style={{ color: "#444" }}>
          Step {current} of {total} ·{" "}
        </span>
        <span style={{ color: "#D4AF37" }}>{activeStep.label}</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SmartFillUploader: discoverable contract upload at the top of Step 1.
// Uses the existing /documents/upload + /documents/{id}/extract-property
// flow. On success, fields in Steps 1–3 are auto-populated.
// ---------------------------------------------------------------------------
function SmartFillUploader({ onExtracted, sym }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(""); // "uploading" | "extracting" | ""

  const trigger = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-selecting same file
    setBusy(true);
    try {
      setStage("uploading");
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({ auto_classify: "true" });
      const { data: doc } = await api.post(
        `/documents/upload?${qs.toString()}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setStage("extracting");
      const { data } = await api.post(`/documents/${doc.id}/extract-property`);
      if (data?.extracted && !data.extracted.error) {
        onExtracted(data.extracted);
        toast.success("Smart-Fill complete", {
          description: "Review the auto-filled fields below before continuing.",
        });
      } else {
        toast.warning("We couldn't read this contract", {
          description:
            "No property details were extracted. Please fill in the form manually.",
        });
      }
    } catch (err) {
      toast.error("Smart-Fill failed", {
        description: err.response?.data?.detail || "Upload or extraction error.",
      });
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  return (
    <div data-testid="smart-fill-zone">
      <div
        className="w-full rounded-lg flex flex-col sm:flex-row gap-3 sm:gap-5 items-start sm:items-center"
        style={{
          border: "1px dashed #2a2a2a",
          padding: "20px 24px",
          background: "rgba(212,175,55,0.04)",
        }}
      >
        <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
          <UploadCloud
            className="w-5 h-5 shrink-0 mt-0.5 sm:mt-0"
            style={{ color: "#D4AF37" }}
          />
          <div className="min-w-0">
            <p
              className="leading-snug"
              style={{
                fontFamily: "Georgia, serif",
                fontSize: "15px",
                color: "#F5F0E8",
              }}
            >
              Have your purchase contract?
            </p>
            <p
              className="mt-1"
              style={{
                fontFamily: "Arial, sans-serif",
                fontSize: "12px",
                color: "#777",
              }}
            >
              Upload it and AI will fill this form automatically.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="smart-fill-upload-btn"
          onClick={trigger}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-2 transition-colors disabled:opacity-60"
          style={{
            border: "0.5px solid #D4AF37",
            color: "#D4AF37",
            background: "transparent",
            borderRadius: "6px",
            padding: "8px 20px",
            fontSize: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          {busy ? (
            <>
              <Sparkles className="w-3.5 h-3.5 animate-pulse" />
              {stage === "uploading" ? "Uploading…" : "Reading…"}
            </>
          ) : (
            <>Upload Contract</>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
          data-testid="smart-fill-file-input"
        />
      </div>

      {/* "or fill in manually" divider */}
      <div
        className="flex items-center gap-3 my-5"
        aria-hidden="true"
        data-testid="smart-fill-divider"
      >
        <span className="flex-1 h-px" style={{ background: "#1e1e1e" }} />
        <span
          style={{
            fontSize: "11px",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "#333",
          }}
        >
          or fill in manually
        </span>
        <span className="flex-1 h-px" style={{ background: "#1e1e1e" }} />
      </div>
      {/* Suppress unused-var warning while keeping `sym` available for future
          contextual hints inside the uploader. */}
      <span className="hidden">{sym}</span>
    </div>
  );
}

const VALUATION_SOURCES = [
  { v: "", label: "Not specified" },
  { v: "owner_estimate", label: "Owner Estimate" },
  { v: "agent_valuation", label: "Agent Valuation" },
  { v: "professional_appraisal", label: "Professional Appraisal" },
  { v: "other", label: "Other" },
];

const initialFormState = {
  name: "",
  location: "dubai",
  currency: "AED",
  purchase_price: "",
  purchase_date: "",
  property_type: "residential",
  description: "",
  area_sqft: "",
  monthly_rental_income: "",
  service_charges_annual: "",
  service_fee_per_sqft: "",
  maintenance_per_sqft: "",
  other_annual_costs: "",
  other_purchase_costs: "",
  broker_commission: "",
  dld_fee: "",
  trustee_fee: "",
  is_financed: false,
  loan_amount: "",
  interest_rate_pct: "",
  amortization_pct: "",
  // North Cyprus
  is_new_build: false,
  transformer_fee: "",
  solicitor_fee: "",
  property_tax_annual: "",
  stamp_duty: "",
  vat: "",
  transfer_fee: "",
  // Germany
  bundesland: "bayern",
  grunderwerbsteuer: "",
  notary_fee: "",
  renovation_cost: "",
  hausgeld_monthly: "",
  hausgeld_umlage_monthly: "",
  hausgeld_nicht_umlage_monthly: "",
  instandhaltungsruecklage_monthly: "",
  // Dubai Excel-parity fields (Feb 2026)
  furniture_cost: "",
  cheque_fee: "",
  financing_setup_costs: "",
  wire_transfer_fee: "",
  // Deal-Analyzer-style additive opex (May 2026 harmonisation)
  insurance_annual: 0,
  management_fee_pct: 10,
  maintenance_reserve_pct: 1,
  vacancy_reserve_pct: 2,
  // Loan amortisation style + STR income (Feb 2026 harmonisation)
  amortization_type: "amortizing",
  income_mode: "long_term",
  nightly_rate: "",
  occupancy_pct: 65,
  utilities_annual: "",
  cleaning_monthly: "",
  source_document_id: "",
  // Optional current-valuation tracking (ALL property types). Purely for
  // portfolio reporting — kept fully separate from purchase price / paid-in
  // capital and never fed into any fee/ROI/tax engine.
  estimated_market_value: "",
  market_valuation_date: "",
  valuation_source: "",
};

export default function AddAssetDialog({ open, onClose, onSuccess, asset = null, enableCountryPicker = false }) {
  const isEditMode = !!asset;
  const [form, setForm] = useState(initialFormState);
  const [roi, setRoi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateOpen, setDateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [valDateOpen, setValDateOpen] = useState(false);

  // 4-step wizard state — when editing an existing asset, skip Smart-Fill
  // and land directly on the Acquisition step (Step 2) so users see the
  // cost breakdown immediately. Smart-Fill upload only makes sense for
  // fresh acquisitions.
  const [currentStep, setCurrentStep] = useState(1);
  const [showAdditional, setShowAdditional] = useState(false);

  // Country-picker gate — opt-in via `enableCountryPicker` (new /portfolio
  // dashboard only). "picker" shows the market selection screen, "other"
  // shows the Beta simplified-tracking entry point, "wizard" is the original
  // 5-step flow. Legacy /dashboard never passes the prop, so it stays on
  // "wizard" and its Add Asset experience is byte-for-byte unchanged.
  const [view, setView] = useState("wizard");
  // An existing simplified-global asset ("Somewhere Else") must reopen in the
  // simplified form — never the country wizard — regardless of how the dialog
  // was launched (edit always passes `asset`, never `enableCountryPicker`).
  const isSimpleAsset =
    isEditMode &&
    (asset?.registration_mode === "simplified_global" || asset?.location === "other");
  useEffect(() => {
    if (!open) return;
    if (isSimpleAsset) setView("other");
    else setView(enableCountryPicker && !isEditMode ? "picker" : "wizard");
  }, [open, enableCountryPicker, isEditMode, isSimpleAsset]);

  // Override state for auto-calculated fields. False -> show computed value.
  // In edit mode, any persisted custom value should default to "overriding"
  // so the user sees their actual number, not the system recalculation.
  const [overrides, setOverrides] = useState({
    dld_fee: false,
    broker_commission: false,
    trustee_fee: false,
    grunderwerbsteuer: false,
    notary_fee: false,
    stamp_duty: false,
    vat: false,
    transfer_fee: false,
  });

  // "Estimate later" deferred state for optional rent / Hausgeld.
  const [deferred, setDeferred] = useState({
    monthly_rental_income: false,
    hausgeld: false,
  });

  // Hydrate the form whenever a new asset is passed in (edit mode).
  // Runs only on `asset.id` change so the user can freely edit without their
  // typed values being clobbered by an unrelated re-render.
  useEffect(() => {
    if (!isEditMode || !asset?.id) return;
    const toStr = (v) => (v == null || v === "" ? "" : String(v));
    setForm({
      ...initialFormState,
      name: asset.name || "",
      location: asset.location || "dubai",
      currency:
        asset.currency ||
        (asset.location === "germany" ? "EUR" : asset.location === "north_cyprus" ? "GBP" : "AED"),
      purchase_price: toStr(asset.purchase_price),
      purchase_date: asset.purchase_date || "",
      property_type: asset.property_type || "residential",
      description: asset.description || "",
      area_sqft: toStr(asset.area_sqft),
      monthly_rental_income: toStr(asset.monthly_rental_income),
      service_charges_annual: toStr(asset.service_charges_annual),
      service_fee_per_sqft: toStr(asset.service_fee_per_sqft),
      maintenance_per_sqft: toStr(asset.maintenance_per_sqft),
      other_annual_costs: toStr(asset.other_annual_costs),
      other_purchase_costs: toStr(asset.other_purchase_costs),
      broker_commission: toStr(asset.broker_commission),
      dld_fee: toStr(asset.dld_fee),
      trustee_fee: toStr(asset.trustee_fee),
      is_financed: !!asset.is_financed,
      loan_amount: toStr(asset.loan_amount),
      interest_rate_pct: toStr(asset.interest_rate_pct),
      amortization_pct: toStr(asset.amortization_pct),
      is_new_build: !!asset.is_new_build,
      transformer_fee: toStr(asset.transformer_fee),
      solicitor_fee: toStr(asset.solicitor_fee),
      property_tax_annual: toStr(asset.property_tax_annual),
      stamp_duty: toStr(asset.stamp_duty),
      vat: toStr(asset.vat),
      transfer_fee: toStr(asset.transfer_fee),
      bundesland: asset.bundesland || "bayern",
      grunderwerbsteuer: toStr(asset.grunderwerbsteuer),
      notary_fee: toStr(asset.notary_fee),
      renovation_cost: toStr(asset.renovation_cost),
      hausgeld_monthly: toStr(asset.hausgeld_monthly),
      hausgeld_umlage_monthly: toStr(asset.hausgeld_umlage_monthly),
      hausgeld_nicht_umlage_monthly: toStr(asset.hausgeld_nicht_umlage_monthly),
      instandhaltungsruecklage_monthly: toStr(asset.instandhaltungsruecklage_monthly),
      furniture_cost: toStr(asset.furniture_cost),
      cheque_fee: toStr(asset.cheque_fee),
      financing_setup_costs: toStr(asset.financing_setup_costs),
      wire_transfer_fee: toStr(asset.wire_transfer_fee),
      // Edit mode must PRESERVE the stored values — never fall back to the
      // create-time defaults (10/1/2), otherwise opening & saving a property
      // whose opex was unset would silently inject those percentages and change
      // running costs / cashflow / ROI. Defaults apply on CREATE via
      // initialFormState only. Empty here -> saved as 0 (no cost added).
      insurance_annual: toStr(asset.insurance_annual),
      management_fee_pct: toStr(asset.management_fee_pct),
      maintenance_reserve_pct: toStr(asset.maintenance_reserve_pct),
      vacancy_reserve_pct: toStr(asset.vacancy_reserve_pct),
      amortization_type: asset.amortization_type || "amortizing",
      income_mode: asset.income_mode || "long_term",
      nightly_rate: toStr(asset.nightly_rate),
      occupancy_pct: asset.occupancy_pct ?? 65,
      utilities_annual: toStr(asset.utilities_annual),
      cleaning_monthly: toStr(asset.cleaning_monthly),
      source_document_id: asset.source_document_id || "",
      estimated_market_value: toStr(asset.estimated_market_value),
      market_valuation_date: asset.market_valuation_date || "",
      valuation_source: asset.valuation_source || "",
    });
    if (asset.purchase_date) {
      const parsed = new Date(asset.purchase_date + "T00:00:00");
      if (!Number.isNaN(parsed.getTime())) setSelectedDate(parsed);
    }
    // Persisted custom fees → show as "overriding" so the user sees their
    // actual stored number rather than a fresh auto-calculation.
    setOverrides({
      dld_fee: asset.dld_fee != null && asset.dld_fee !== "",
      broker_commission:
        asset.broker_commission != null && asset.broker_commission !== "",
      trustee_fee: asset.trustee_fee != null && asset.trustee_fee !== "",
      grunderwerbsteuer:
        asset.grunderwerbsteuer != null && asset.grunderwerbsteuer !== "",
      notary_fee: asset.notary_fee != null && asset.notary_fee !== "",
      stamp_duty: asset.stamp_duty != null && asset.stamp_duty !== "",
      vat: asset.vat != null && asset.vat !== "",
      transfer_fee: asset.transfer_fee != null && asset.transfer_fee !== "",
    });
    setCurrentStep(1);
    setShowAdditional(true); // surface renovation / additional costs immediately
    setError("");
  }, [asset?.id, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDubai = form.location === "dubai";
  const isCyprus = form.location === "north_cyprus";
  const isGermany = form.location === "germany";
  const sym = currencySymbols[form.currency] || form.currency;
  const areaUnit = isCyprus || isGermany ? "m²" : "sq ft";
  const ph = PLACEHOLDERS[form.location] || PLACEHOLDERS.dubai;

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);


  // Live ROI calculation
  const calculateRoi = useCallback(async () => {
    const price = parseFloat(form.purchase_price);
    if (!price) {
      setRoi(null);
      return;
    }
    try {
      const { data } = await api.post("/assets/calculate-roi", {
        purchase_price: price,
        monthly_rental_income: parseFloat(form.monthly_rental_income) || 0,
        location: form.location,
        area_sqft: parseFloat(form.area_sqft) || 0,
        service_fee_per_sqft: parseFloat(form.service_fee_per_sqft) || 0,
        maintenance_per_sqft: parseFloat(form.maintenance_per_sqft) || 0,
        service_charges_annual: parseFloat(form.service_charges_annual) || 0,
        other_annual_costs: parseFloat(form.other_annual_costs) || 0,
        other_purchase_costs: parseFloat(form.other_purchase_costs) || 0,
        broker_commission: parseFloat(form.broker_commission) || undefined,
        dld_fee: parseFloat(form.dld_fee) || undefined,
        trustee_fee: parseFloat(form.trustee_fee) || undefined,
        is_financed: form.is_financed,
        loan_amount: parseFloat(form.loan_amount) || 0,
        interest_rate_pct: parseFloat(form.interest_rate_pct) || 0,
        amortization_pct: parseFloat(form.amortization_pct) || 0,
        is_new_build: form.is_new_build,
        transformer_fee: parseFloat(form.transformer_fee) || 0,
        solicitor_fee: parseFloat(form.solicitor_fee) || 0,
        property_tax_annual: parseFloat(form.property_tax_annual) || 0,
        stamp_duty: form.stamp_duty !== "" ? parseFloat(form.stamp_duty) : undefined,
        vat: form.vat !== "" ? parseFloat(form.vat) : undefined,
        transfer_fee: form.transfer_fee !== "" ? parseFloat(form.transfer_fee) : undefined,
        bundesland: form.bundesland,
        grunderwerbsteuer: parseFloat(form.grunderwerbsteuer) || undefined,
        notary_fee: parseFloat(form.notary_fee) || undefined,
        renovation_cost: parseFloat(form.renovation_cost) || 0,
        hausgeld_monthly: parseFloat(form.hausgeld_monthly) || 0,
        hausgeld_umlage_monthly:
          form.hausgeld_umlage_monthly !== ""
            ? parseFloat(form.hausgeld_umlage_monthly)
            : null,
        hausgeld_nicht_umlage_monthly:
          form.hausgeld_nicht_umlage_monthly !== ""
            ? parseFloat(form.hausgeld_nicht_umlage_monthly)
            : null,
        instandhaltungsruecklage_monthly:
          parseFloat(form.instandhaltungsruecklage_monthly) || 0,
        furniture_cost: parseFloat(form.furniture_cost) || 0,
        cheque_fee: parseFloat(form.cheque_fee) || 0,
        financing_setup_costs: parseFloat(form.financing_setup_costs) || 0,
        wire_transfer_fee:
          form.wire_transfer_fee !== "" && form.wire_transfer_fee !== null
            ? parseFloat(form.wire_transfer_fee)
            : null,
        insurance_annual: parseFloat(form.insurance_annual) || 0,
        management_fee_pct: parseFloat(form.management_fee_pct) || 0,
        maintenance_reserve_pct: parseFloat(form.maintenance_reserve_pct) || 0,
        vacancy_reserve_pct: parseFloat(form.vacancy_reserve_pct) || 0,
        amortization_type: form.amortization_type || "amortizing",
        income_mode: form.income_mode || "long_term",
        nightly_rate: parseFloat(form.nightly_rate) || 0,
        occupancy_pct: parseFloat(form.occupancy_pct) || 0,
        utilities_annual: parseFloat(form.utilities_annual) || 0,
        cleaning_monthly: parseFloat(form.cleaning_monthly) || 0,
      });
      setRoi(data);
    } catch {
      setRoi(null);
    }
  }, [
    form.purchase_price,
    form.monthly_rental_income,
    form.location,
    form.area_sqft,
    form.service_fee_per_sqft,
    form.maintenance_per_sqft,
    form.service_charges_annual,
    form.other_annual_costs,
    form.other_purchase_costs,
    form.broker_commission,
    form.dld_fee,
    form.trustee_fee,
    form.is_financed,
    form.loan_amount,
    form.interest_rate_pct,
    form.amortization_pct,
    form.is_new_build,
    form.transformer_fee,
    form.solicitor_fee,
    form.property_tax_annual,
    form.stamp_duty,
    form.vat,
    form.transfer_fee,
    form.bundesland,
    form.grunderwerbsteuer,
    form.notary_fee,
    form.renovation_cost,
    form.hausgeld_monthly,
    form.hausgeld_umlage_monthly,
    form.hausgeld_nicht_umlage_monthly,
    form.instandhaltungsruecklage_monthly,
    form.furniture_cost,
    form.cheque_fee,
    form.financing_setup_costs,
    form.wire_transfer_fee,
    form.insurance_annual,
    form.management_fee_pct,
    form.maintenance_reserve_pct,
    form.vacancy_reserve_pct,
    form.amortization_type,
    form.income_mode,
    form.nightly_rate,
    form.occupancy_pct,
    form.utilities_annual,
    form.cleaning_monthly,
  ]);

  useEffect(() => {
    const t = setTimeout(calculateRoi, 300);
    return () => clearTimeout(t);
  }, [calculateRoi]);

  const resetForm = () => {
    setForm(initialFormState);
    setRoi(null);
    setSelectedDate(undefined);
    setError("");
    setCurrentStep(1);
    setShowAdditional(false);
    setOverrides({
      dld_fee: false,
      broker_commission: false,
      trustee_fee: false,
      grunderwerbsteuer: false,
      notary_fee: false,
      stamp_duty: false,
      vat: false,
      transfer_fee: false,
    });
    setDeferred({ monthly_rental_income: false, hausgeld: false });
  };

  // Apply AI-extracted fields from Smart-Fill onto the form, keeping
  // sensible defaults for anything the contract didn't reveal.
  const applyExtracted = (ex) => {
    if (!ex) return;
    setForm((prev) => {
      const next = { ...prev };
      const set = (k, v) => {
        if (v !== undefined && v !== null && v !== "") next[k] = String(v);
      };
      // Direct string fields
      if (ex.name) next.name = ex.name;
      if (ex.location && ["dubai", "north_cyprus", "germany"].includes(ex.location)) {
        next.location = ex.location;
      }
      if (ex.currency && ["AED", "GBP", "EUR", "TRY"].includes(ex.currency)) {
        next.currency = ex.currency;
      } else {
        next.currency =
          next.location === "dubai" ? "AED" : next.location === "germany" ? "EUR" : "GBP";
      }
      if (ex.property_type && ["residential", "commercial", "off_plan"].includes(ex.property_type)) {
        next.property_type = ex.property_type;
      }
      if (ex.description) next.description = ex.description;
      if (ex.purchase_date) {
        next.purchase_date = ex.purchase_date;
        const parsed = new Date(`${ex.purchase_date}T00:00:00`);
        if (!Number.isNaN(parsed.getTime())) setSelectedDate(parsed);
      }
      set("purchase_price", ex.purchase_price);
      set("area_sqft", ex.area_sqft);
      set("monthly_rental_income", ex.monthly_rental_income);
      set("service_charges_annual", ex.service_charges_annual);
      if (ex.source_document_id) next.source_document_id = ex.source_document_id;
      return next;
    });
    // Reset overrides + deferred so user sees fresh auto-calcs.
    setOverrides({
      dld_fee: false,
      broker_commission: false,
      trustee_fee: false,
      grunderwerbsteuer: false,
      notary_fee: false,
      stamp_duty: false,
      vat: false,
      transfer_fee: false,
    });
    setDeferred({ monthly_rental_income: false, hausgeld: false });
  };

  // Step-level validation. Returns "" if OK, else an error message.
  const validateStep = (step) => {
    if (step >= 1) {
      if (!form.name) return "Please add a property name.";
      if (!form.purchase_price) return "Please add a purchase price.";
      if (!form.purchase_date) return "Please select a purchase date.";
    }
    if (step >= 4 && form.is_financed) {
      const loan = parseFloat(form.loan_amount);
      const interest = parseFloat(form.interest_rate_pct);
      if (!loan || loan <= 0) return "Bank Financing is ON but Loan Amount is missing.";
      if (!interest || interest <= 0)
        return "Bank Financing is ON but Interest Rate is missing.";
    }
    return "";
  };

  const goNext = () => {
    const err = validateStep(currentStep);
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setCurrentStep((s) => Math.min(5, s + 1));
  };
  const goBack = () => {
    setError("");
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError("");

    const err = validateStep(5);
    if (err) {
      setError(err);
      if (err.includes("name") || err.includes("price") || err.includes("date")) {
        setCurrentStep(1);
      } else if (err.includes("Loan") || err.includes("Interest")) {
        setCurrentStep(4);
      }
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        purchase_price: parseFloat(form.purchase_price),
        area_sqft: form.area_sqft ? parseFloat(form.area_sqft) : null,
        monthly_rental_income: form.monthly_rental_income
          ? parseFloat(form.monthly_rental_income)
          : null,
        service_charges_annual: form.service_charges_annual
          ? parseFloat(form.service_charges_annual)
          : null,
        service_fee_per_sqft: form.service_fee_per_sqft
          ? parseFloat(form.service_fee_per_sqft)
          : null,
        maintenance_per_sqft: form.maintenance_per_sqft
          ? parseFloat(form.maintenance_per_sqft)
          : null,
        other_annual_costs: form.other_annual_costs
          ? parseFloat(form.other_annual_costs)
          : null,
        other_purchase_costs: form.other_purchase_costs
          ? parseFloat(form.other_purchase_costs)
          : null,
        broker_commission: form.broker_commission
          ? parseFloat(form.broker_commission)
          : null,
        dld_fee: form.dld_fee ? parseFloat(form.dld_fee) : null,
        trustee_fee: form.trustee_fee ? parseFloat(form.trustee_fee) : null,
        is_financed: form.is_financed,
        loan_amount: form.loan_amount ? parseFloat(form.loan_amount) : null,
        interest_rate_pct: form.interest_rate_pct
          ? parseFloat(form.interest_rate_pct)
          : null,
        amortization_pct: form.amortization_pct
          ? parseFloat(form.amortization_pct)
          : null,
        is_new_build: form.is_new_build,
        transformer_fee: form.transformer_fee
          ? parseFloat(form.transformer_fee)
          : null,
        solicitor_fee: form.solicitor_fee ? parseFloat(form.solicitor_fee) : null,
        property_tax_annual: form.property_tax_annual
          ? parseFloat(form.property_tax_annual)
          : null,
        stamp_duty: form.stamp_duty ? parseFloat(form.stamp_duty) : null,
        vat: form.vat ? parseFloat(form.vat) : null,
        transfer_fee: form.transfer_fee ? parseFloat(form.transfer_fee) : null,
        bundesland: form.bundesland || null,
        grunderwerbsteuer: form.grunderwerbsteuer
          ? parseFloat(form.grunderwerbsteuer)
          : null,
        notary_fee: form.notary_fee ? parseFloat(form.notary_fee) : null,
        renovation_cost: form.renovation_cost
          ? parseFloat(form.renovation_cost)
          : null,
        hausgeld_monthly: form.hausgeld_monthly
          ? parseFloat(form.hausgeld_monthly)
          : null,
        hausgeld_umlage_monthly:
          form.hausgeld_umlage_monthly !== ""
            ? parseFloat(form.hausgeld_umlage_monthly)
            : null,
        hausgeld_nicht_umlage_monthly:
          form.hausgeld_nicht_umlage_monthly !== ""
            ? parseFloat(form.hausgeld_nicht_umlage_monthly)
            : null,
        instandhaltungsruecklage_monthly: form.instandhaltungsruecklage_monthly
          ? parseFloat(form.instandhaltungsruecklage_monthly)
          : null,
        furniture_cost: form.furniture_cost ? parseFloat(form.furniture_cost) : null,
        cheque_fee: form.cheque_fee ? parseFloat(form.cheque_fee) : null,
        financing_setup_costs: form.financing_setup_costs
          ? parseFloat(form.financing_setup_costs)
          : null,
        wire_transfer_fee:
          form.wire_transfer_fee !== "" && form.wire_transfer_fee !== null
            ? parseFloat(form.wire_transfer_fee)
            : null,
        insurance_annual: parseFloat(form.insurance_annual) || 0,
        management_fee_pct: parseFloat(form.management_fee_pct) || 0,
        maintenance_reserve_pct: parseFloat(form.maintenance_reserve_pct) || 0,
        vacancy_reserve_pct: parseFloat(form.vacancy_reserve_pct) || 0,
        amortization_type: form.amortization_type || "amortizing",
        income_mode: form.income_mode || "long_term",
        nightly_rate: parseFloat(form.nightly_rate) || 0,
        occupancy_pct: parseFloat(form.occupancy_pct) || 0,
        utilities_annual: parseFloat(form.utilities_annual) || 0,
        cleaning_monthly: parseFloat(form.cleaning_monthly) || 0,
        estimated_market_value:
          form.estimated_market_value !== "" ? parseFloat(form.estimated_market_value) : null,
        market_valuation_date: form.market_valuation_date || null,
        valuation_source: form.valuation_source || null,
      };
      if (isEditMode) {
        await api.put(`/assets/${asset.id}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/assets", payload);
      }
      onSuccess();
      onClose();
      // Only reset when creating; in edit mode the parent owns the asset
      // identity and will re-hydrate on next open via the useEffect above.
      if (!isEditMode) resetForm();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d
            .map(
              (e) =>
                `${Array.isArray(e.loc) ? e.loc.slice(-1)[0] : ""}: ${e.msg}`
            )
            .join("; ")
        : typeof d === "string"
        ? d
        : "Failed to add asset";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "location") {
        updated.currency =
          value === "dubai" ? "AED" : value === "germany" ? "EUR" : "GBP";
        // Reset overrides + deferred when location flips so user sees fresh auto-calcs.
        setOverrides({
          dld_fee: false,
          broker_commission: false,
          trustee_fee: false,
          grunderwerbsteuer: false,
          notary_fee: false,
          stamp_duty: false,
          vat: false,
          transfer_fee: false,
        });
        setDeferred({ monthly_rental_income: false, hausgeld: false });
      }
      return updated;
    });
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setForm((prev) => ({ ...prev, purchase_date: format(date, "yyyy-MM-dd") }));
      setDateOpen(false);
    }
  };

  const inputCls =
    "w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none";
  const monoInputCls =
    "w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none";
  const labelCls =
    "text-[10px] tracking-[0.15em] uppercase text-white mb-1.5 block";
  const selectCls =
    "w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none";
  const skipLinkCls =
    "text-[10px] tracking-[0.12em] uppercase text-white/55 hover:text-white/90 transition-colors mt-1.5";

  // ─────────────────────────────────────────────────────────────────────
  // Step 4 — read-only review helpers
  // ─────────────────────────────────────────────────────────────────────
  const fmtMoney = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? `${sym} ${Math.round(Number(n)).toLocaleString()}`
      : "—";
  const locationLabel = isGermany
    ? "Germany · EUR"
    : isCyprus
    ? "North Cyprus · GBP"
    : "Dubai, UAE · AED";

  const headerSubtitle =
    view === "picker"
      ? "Select a market"
      : view === "other"
      ? "Simplified Tracking · Beta"
      : locationLabel;

  // Picker → wizard: reuse updateField so currency + fee overrides reset
  // exactly as the original location dropdown did. No workflow change.
  const chooseCountry = (loc) => {
    updateField("location", loc);
    setCurrentStep(1);
    setError("");
    setView("wizard");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6"
          >
            <div
              className="bg-[#0A0A0A] border-t sm:border border-white/[0.12] w-full max-w-2xl max-h-[92vh] sm:max-h-[92vh] overflow-y-auto rounded-t-xl sm:rounded-none"
              data-testid="add-asset-dialog"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              {/* Sticky header */}
              <div className="flex items-center justify-between p-5 border-b border-white/[0.10] sticky top-0 bg-[#0A0A0A] z-10">
                <div>
                  <h2 className="font-serif text-lg text-white">
                    {isEditMode ? "Edit Property Asset" : "Add Property Asset"}
                  </h2>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/55 mt-1">
                    {headerSubtitle}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/65 hover:text-white transition-colors"
                  data-testid="close-dialog-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Country picker (opt-in) — market selection gate */}
              {view === "picker" && (
                <CountryPicker
                  onChoose={chooseCountry}
                  onChooseOther={() => {
                    setError("");
                    setView("other");
                  }}
                />
              )}

              {/* Beta simplified-tracking registration (worldwide markets) */}
              {view === "other" && (
                <SimpleGlobalForm
                  asset={isSimpleAsset ? asset : null}
                  onBack={isEditMode ? null : () => setView("picker")}
                  onClose={onClose}
                  onSuccess={onSuccess}
                />
              )}

              {/* Wizard stepper */}
              {view === "wizard" && (
                <>
                  <WizardStepper current={currentStep} />

                  {error && (
                    <div
                      className="mx-5 mt-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 text-sm"
                      data-testid="asset-form-error"
                    >
                      {error}
                    </div>
                  )}

              {/* `noValidate` + a no-op onSubmit prevent two foot-guns that
                  affect step-based wizards:
                  1. Enter pressed inside a text input would submit the form.
                  2. A React re-render between a button's mousedown and mouseup
                     can retarget the click to a different child element. */}
              <form
                onSubmit={(e) => e.preventDefault()}
                noValidate
                className="p-5 space-y-5"
              >
                {/* ============================================================ */}
                {/* STEP 1 · PROPERTY                                            */}
                {/* ============================================================ */}
                {currentStep === 1 && (
                  <div className="space-y-4" data-testid="step-1-content">
                    {/* Smart-Fill upload zone */}
                    {/* Smart-Fill upload zone (hidden in edit mode — only
                        useful for fresh acquisitions). */}
                    {!isEditMode && (
                      <SmartFillUploader onExtracted={applyExtracted} sym={sym} />
                    )}

                    {/* Property Name */}
                    <div>
                      <label className={labelCls}>Property Name</label>
                      <input
                        data-testid="asset-name-input"
                        value={form.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        className={inputCls}
                        placeholder={`e.g. ${ph.name}`}
                        required
                      />
                    </div>

                    {/* Location + Currency + Type */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Location</label>
                        <select
                          data-testid="asset-location-select"
                          value={form.location}
                          onChange={(e) => updateField("location", e.target.value)}
                          className={selectCls}
                        >
                          <option value="dubai">Dubai, UAE</option>
                          <option value="north_cyprus">North Cyprus</option>
                          <option value="germany">Germany</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Currency</label>
                        <select
                          data-testid="asset-currency-select"
                          value={form.currency}
                          onChange={(e) => updateField("currency", e.target.value)}
                          className={selectCls}
                        >
                          <option value="AED">AED</option>
                          <option value="GBP">GBP</option>
                          <option value="EUR">EUR</option>
                          <option value="TRY">TRY</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Type</label>
                        <select
                          data-testid="asset-type-select"
                          value={form.property_type}
                          onChange={(e) =>
                            updateField("property_type", e.target.value)
                          }
                          className={selectCls}
                        >
                          <option value="residential">Residential</option>
                          <option value="commercial">Commercial</option>
                          <option value="off_plan">Off Plan</option>
                        </select>
                      </div>
                    </div>

                    {/* Price + Date + Area */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Purchase Price ({sym})</label>
                        <input
                          data-testid="asset-price-input"
                          type="number"
                          step="0.01"
                          value={form.purchase_price}
                          onChange={(e) =>
                            updateField("purchase_price", e.target.value)
                          }
                          className={monoInputCls}
                          placeholder={ph.price}
                          required
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Purchase Date</label>
                        <Popover open={dateOpen} onOpenChange={setDateOpen}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              data-testid="asset-date-picker-btn"
                              className="w-full bg-[#050505] border border-white/10 text-left px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none flex items-center justify-between hover:border-white/20 transition-colors"
                            >
                              <span
                                className={
                                  form.purchase_date ? "text-white" : "text-white/55"
                                }
                              >
                                {form.purchase_date
                                  ? format(
                                      new Date(form.purchase_date + "T00:00:00"),
                                      "MMM dd, yyyy"
                                    )
                                  : "Select date"}
                              </span>
                              <CalendarDays className="w-4 h-4 text-white/65" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0 bg-[#0A0A0A] border border-white/10"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={selectedDate}
                              onSelect={handleDateSelect}
                              initialFocus
                              captionLayout="dropdown-buttons"
                              fromYear={new Date().getFullYear() - 50}
                              toYear={new Date().getFullYear() + 1}
                              defaultMonth={selectedDate || new Date()}
                              classNames={{
                                caption_label: "hidden",
                                caption_dropdowns: "flex gap-2 items-center",
                                dropdown:
                                  "bg-[#0A0A0A] text-white text-sm border border-white/10 hover:border-[#D4AF37]/40 px-2 py-1 outline-none cursor-pointer",
                                dropdown_month: "relative",
                                dropdown_year: "relative",
                                vhidden: "hidden",
                              }}
                              className="[&_.rdp-day_selected]:bg-[#D4AF37] [&_.rdp-day_selected]:text-black [&_.rdp-head_cell]:text-white/65 [&_.rdp-caption]:text-white [&_.rdp-nav_button]:text-white/60 [&_.rdp-nav_button:hover]:text-white [&_.rdp-cell]:text-white/85 [&_.rdp-day:hover]:bg-white/10 [&_.rdp-day_today]:border [&_.rdp-day_today]:border-[#D4AF37]/40"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className={labelCls}>Area ({areaUnit})</label>
                        <input
                          data-testid="asset-area-input"
                          type="number"
                          value={form.area_sqft}
                          onChange={(e) => updateField("area_sqft", e.target.value)}
                          className={monoInputCls}
                          placeholder={`e.g. ${ph.area}`}
                        />
                      </div>
                    </div>

                    {/* Current valuation — optional, reporting only. Never
                        touches purchase price / paid-in capital / fee engines. */}
                    <div className="border border-white/10 bg-white/[0.02] p-4" data-testid="valuation-section">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[11px] tracking-[0.15em] uppercase text-[#D4AF37]">Current Valuation</span>
                        <span className="text-[10px] tracking-[0.12em] uppercase text-white/40">Optional</span>
                      </div>
                      <p className="text-white/45 text-[11px] mb-3">
                        For portfolio tracking only. Separate from purchase price — never used in fee or ROI calculations.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className={labelCls}>Est. Market Value ({sym})</label>
                          <input
                            data-testid="asset-valuation-value-input"
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.estimated_market_value}
                            onChange={(e) => updateField("estimated_market_value", e.target.value)}
                            className={monoInputCls}
                            placeholder="e.g. 275000"
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Valuation Date</label>
                          <Popover open={valDateOpen} onOpenChange={setValDateOpen}>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                data-testid="asset-valuation-date-btn"
                                className="w-full bg-[#050505] border border-white/10 text-left px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none flex items-center justify-between hover:border-white/20 transition-colors"
                              >
                                <span className={form.market_valuation_date ? "text-white" : "text-white/55"}>
                                  {form.market_valuation_date
                                    ? format(new Date(form.market_valuation_date + "T00:00:00"), "MMM dd, yyyy")
                                    : "Select date"}
                                </span>
                                <CalendarDays className="w-4 h-4 text-white/65" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border border-white/10" align="start">
                              <Calendar
                                mode="single"
                                selected={form.market_valuation_date ? new Date(form.market_valuation_date + "T00:00:00") : undefined}
                                onSelect={(d) => {
                                  if (d) {
                                    updateField("market_valuation_date", format(d, "yyyy-MM-dd"));
                                    setValDateOpen(false);
                                  }
                                }}
                                initialFocus
                                captionLayout="dropdown-buttons"
                                fromYear={new Date().getFullYear() - 50}
                                toYear={new Date().getFullYear() + 1}
                                defaultMonth={form.market_valuation_date ? new Date(form.market_valuation_date + "T00:00:00") : new Date()}
                                classNames={{
                                  caption_label: "hidden",
                                  caption_dropdowns: "flex gap-2 items-center",
                                  dropdown:
                                    "bg-[#0A0A0A] text-white text-sm border border-white/10 hover:border-[#D4AF37]/40 px-2 py-1 outline-none cursor-pointer",
                                  dropdown_month: "relative",
                                  dropdown_year: "relative",
                                  vhidden: "hidden",
                                }}
                                className="[&_.rdp-day_selected]:bg-[#D4AF37] [&_.rdp-day_selected]:text-black [&_.rdp-head_cell]:text-white/65 [&_.rdp-caption]:text-white [&_.rdp-nav_button]:text-white/60 [&_.rdp-nav_button:hover]:text-white [&_.rdp-cell]:text-white/85 [&_.rdp-day:hover]:bg-white/10 [&_.rdp-day_today]:border [&_.rdp-day_today]:border-[#D4AF37]/40"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className={labelCls}>Valuation Source</label>
                          <select
                            data-testid="asset-valuation-source-select"
                            value={form.valuation_source}
                            onChange={(e) => updateField("valuation_source", e.target.value)}
                            className={selectCls}
                          >
                            {VALUATION_SOURCES.map((s) => (
                              <option key={s.v} value={s.v} className="bg-[#0A0A0A]">{s.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                  </div>
                )}

                {/* ============================================================ */}
                {/* STEP 2 · ACQUISITION COSTS                                   */}
                {/* ============================================================ */}
                {currentStep === 2 && (
                  <div className="space-y-4" data-testid="step-2-content">
                    {!form.purchase_price && (
                      <div className="text-xs text-white/60 italic">
                        Add a purchase price in Step 1 to see live closing-cost estimates.
                      </div>
                    )}

                    {/* Dubai */}
                    {isDubai && form.purchase_price && (
                      <div
                        className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5"
                        data-testid="dubai-cost-preview"
                      >
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">
                          Dubai · Closing Costs
                        </p>
                        <div className="flex justify-between text-xs">
                          <span className="text-white/80">Purchase Price</span>
                          <span className="font-mono text-white">
                            {sym} {parseFloat(form.purchase_price || 0).toLocaleString()}
                          </span>
                        </div>
                        <AutoCalcRow
                          label="DLD Transfer Fee (4%)"
                          computed={roi?.dld_fee}
                          value={form.dld_fee}
                          onChange={(v) => updateField("dld_fee", v)}
                          sym={sym}
                          isOverriding={overrides.dld_fee}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, dld_fee: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, dld_fee: false }))
                          }
                          testId="asset-dld-input"
                        />
                        <AutoCalcRow
                          label="Brokerage Fees (2.1%)"
                          computed={roi?.broker_commission}
                          value={form.broker_commission}
                          onChange={(v) => updateField("broker_commission", v)}
                          sym={sym}
                          isOverriding={overrides.broker_commission}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, broker_commission: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, broker_commission: false }))
                          }
                          testId="asset-broker-input"
                        />
                        <AutoCalcRow
                          label="Trustee Office Fee"
                          computed={roi?.trustee_fee}
                          value={form.trustee_fee}
                          onChange={(v) => updateField("trustee_fee", v)}
                          sym={sym}
                          isOverriding={overrides.trustee_fee}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, trustee_fee: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, trustee_fee: false }))
                          }
                          testId="asset-trustee-input"
                        />

                        {/* Excel-parity Dubai costs — full-width inline rows */}
                        <div className="pt-3 border-t border-white/[0.09]">
                          <InlineRow label={`Furniture (${sym})`}>
                            <input
                              data-testid="dubai-furniture-input"
                              type="number"
                              step="0.01"
                              value={form.furniture_cost}
                              onChange={(e) => updateField("furniture_cost", e.target.value)}
                              className={inlineInputCls}
                              style={{ ...inlineInputStyle, color: "#FFFFFF" }}
                              placeholder="0"
                            />
                          </InlineRow>

                          <InlineRow label={`Other Financing Setup Costs (${sym})`}>
                            <input
                              data-testid="dubai-financing-setup-input"
                              type="number"
                              step="0.01"
                              value={form.financing_setup_costs}
                              onChange={(e) =>
                                updateField("financing_setup_costs", e.target.value)
                              }
                              className={inlineInputCls}
                              style={{ ...inlineInputStyle, color: "#FFFFFF" }}
                              placeholder="0"
                            />
                          </InlineRow>
                          <p
                            className="pt-1.5 pb-2"
                            style={{ fontSize: "11px", color: "#444" }}
                          >
                            Bank arrangement / valuation / processing
                          </p>

                          <InlineRow label={`Cheque Issuance Fee (${sym})`}>
                            <input
                              data-testid="dubai-cheque-fee-input"
                              type="number"
                              step="0.01"
                              value={form.cheque_fee}
                              onChange={(e) => updateField("cheque_fee", e.target.value)}
                              className={inlineInputCls}
                              style={{ ...inlineInputStyle, color: "#FFFFFF" }}
                              placeholder="0"
                            />
                          </InlineRow>

                          <InlineRow label="Wire Transfer Fee (AED)" isLast>
                            <input
                              data-testid="dubai-wire-fee-input"
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.wire_transfer_fee}
                              onChange={(e) =>
                                updateField("wire_transfer_fee", e.target.value)
                              }
                              className={inlineInputCls}
                              style={{ ...inlineInputStyle, color: "#FFFFFF" }}
                              placeholder="0"
                            />
                          </InlineRow>
                          <p
                            className="pt-1.5"
                            style={{ fontSize: "11px", color: "#444" }}
                          >
                            Type the exact amount your bank charged. No auto-calculation.
                          </p>

                          {roi?.geldtransfer_cost > 0 && (
                            <div className="flex justify-between text-xs pt-3">
                              <span className="text-white/60">Wire transfer (computed)</span>
                              <span className="font-mono text-white/85">
                                {sym} {roi.geldtransfer_cost?.toLocaleString()}
                              </span>
                            </div>
                          )}
                          {roi?.cheque_fee > 0 && (
                            <div className="flex justify-between text-xs pt-1">
                              <span className="text-white/60">Cheque issuance fee</span>
                              <span className="font-mono text-white/85">
                                {sym} {roi.cheque_fee?.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                        {roi && (
                          <div className="flex justify-between text-sm pt-3 border-t border-[#D4AF37]/20">
                            <span className="text-white font-medium">Total Investment</span>
                            <span
                              className="font-mono text-[#D4AF37] font-medium"
                              data-testid="dubai-total-investment"
                            >
                              {sym} {roi.total_investment?.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* North Cyprus */}
                    {isCyprus && form.purchase_price && (
                      <div
                        className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5"
                        data-testid="cyprus-cost-preview"
                      >
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">
                          North Cyprus · Closing Costs
                        </p>

                        <div className="flex items-center justify-between pb-2 border-b border-white/[0.08]">
                          <span className="text-white/85 text-xs">New Build Project</span>
                          <button
                            type="button"
                            onClick={() => updateField("is_new_build", !form.is_new_build)}
                            data-testid="cyprus-newbuild-toggle"
                            className={`w-9 h-5 rounded-full transition-colors relative ${
                              form.is_new_build ? "bg-[#D4AF37]" : "bg-white/10"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                form.is_new_build ? "left-[18px]" : "left-0.5"
                              }`}
                            />
                          </button>
                        </div>

                        <div className="flex justify-between text-xs">
                          <span className="text-white/80">Purchase Price</span>
                          <span className="font-mono text-white">
                            {sym} {parseFloat(form.purchase_price || 0).toLocaleString()}
                          </span>
                        </div>
                        <AutoCalcRow
                          label="Stamp Duty (0.5%)"
                          computed={roi?.stamp_duty}
                          value={form.stamp_duty}
                          onChange={(v) => updateField("stamp_duty", v)}
                          sym={sym}
                          isOverriding={overrides.stamp_duty}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, stamp_duty: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, stamp_duty: false }))
                          }
                          testId="cyprus-stamp-duty-input"
                        />
                        <AutoCalcRow
                          label="VAT (5%)"
                          hint={!form.is_new_build ? "resale, n/a" : undefined}
                          computed={form.is_new_build ? roi?.vat : 0}
                          value={form.vat}
                          onChange={(v) => updateField("vat", v)}
                          sym={sym}
                          isOverriding={overrides.vat}
                          onStartOverride={() => setOverrides((o) => ({ ...o, vat: true }))}
                          onResetOverride={() => setOverrides((o) => ({ ...o, vat: false }))}
                          testId="cyprus-vat-input"
                          disabled={!form.is_new_build}
                        />
                        <AutoCalcRow
                          label="Title Deed Transfer (12%)"
                          computed={roi?.transfer_fee}
                          value={form.transfer_fee}
                          onChange={(v) => updateField("transfer_fee", v)}
                          sym={sym}
                          isOverriding={overrides.transfer_fee}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, transfer_fee: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, transfer_fee: false }))
                          }
                          testId="cyprus-transfer-fee-input"
                        />
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">
                            Transformer Fee{" "}
                            {!form.is_new_build && <span className="text-white/60">— n/a</span>}
                          </span>
                          <div className="flex items-center gap-2">
                            <input
                              data-testid="cyprus-transformer-input"
                              type="number"
                              step="1"
                              value={form.transformer_fee}
                              disabled={!form.is_new_build}
                              onChange={(e) => updateField("transformer_fee", e.target.value)}
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all disabled:opacity-40"
                              placeholder={`e.g. ${ph.transformer}`}
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">Solicitor / Legal Fees</span>
                          <div className="flex items-center gap-2">
                            <input
                              data-testid="cyprus-solicitor-input"
                              type="number"
                              step="1"
                              value={form.solicitor_fee}
                              onChange={(e) => updateField("solicitor_fee", e.target.value)}
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                              placeholder={`e.g. ${ph.solicitor}`}
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">Furniture</span>
                          <div className="flex items-center gap-2">
                            <input
                              data-testid="cyprus-furniture-input"
                              type="number"
                              step="1"
                              value={form.furniture_cost}
                              onChange={(e) => updateField("furniture_cost", e.target.value)}
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                              placeholder="0"
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">Other financing setup costs</span>
                          <div className="flex items-center gap-2">
                            <input
                              data-testid="cyprus-financing-setup-input"
                              type="number"
                              step="1"
                              value={form.financing_setup_costs}
                              onChange={(e) =>
                                updateField("financing_setup_costs", e.target.value)
                              }
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                              placeholder="0"
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">Wire transfer fee</span>
                          <div className="flex items-center gap-2">
                            <input
                              data-testid="cyprus-wire-fee-input"
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.wire_transfer_fee}
                              onChange={(e) =>
                                updateField("wire_transfer_fee", e.target.value)
                              }
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                              placeholder="0"
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">Other Purchase Costs</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="1"
                              value={form.other_purchase_costs}
                              onChange={(e) =>
                                updateField("other_purchase_costs", e.target.value)
                              }
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                              placeholder="0"
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        {roi && (
                          <div className="flex justify-between text-sm pt-3 border-t border-[#D4AF37]/20">
                            <span className="text-white font-medium">Total Investment</span>
                            <span
                              className="font-mono text-[#D4AF37] font-medium"
                              data-testid="cyprus-total-investment"
                            >
                              {sym} {roi.total_investment?.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Germany */}
                    {isGermany && form.purchase_price && (
                      <div
                        className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5"
                        data-testid="germany-cost-preview"
                      >
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">
                          Germany · Closing Costs
                        </p>

                        <div>
                          <label className={labelCls}>
                            Bundesland (Grunderwerbsteuer rate)
                          </label>
                          <select
                            data-testid="germany-bundesland"
                            value={form.bundesland}
                            onChange={(e) => {
                              updateField("bundesland", e.target.value);
                              updateField("grunderwerbsteuer", "");
                              setOverrides((o) => ({ ...o, grunderwerbsteuer: false }));
                            }}
                            className={selectCls}
                          >
                            {BUNDESLAENDER.map((b) => (
                              <option key={b.code} value={b.code}>
                                {b.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex justify-between text-xs pt-1">
                          <span className="text-white/80">Purchase Price</span>
                          <span className="font-mono text-white">
                            {sym} {parseFloat(form.purchase_price || 0).toLocaleString()}
                          </span>
                        </div>

                        <AutoCalcRow
                          label={`Grunderwerbsteuer (${
                            roi?.grunderwerbsteuer_rate_pct
                              ? `${roi.grunderwerbsteuer_rate_pct}%`
                              : "—"
                          })`}
                          computed={roi?.grunderwerbsteuer}
                          value={form.grunderwerbsteuer}
                          onChange={(v) => updateField("grunderwerbsteuer", v)}
                          sym={sym}
                          isOverriding={overrides.grunderwerbsteuer}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, grunderwerbsteuer: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, grunderwerbsteuer: false }))
                          }
                          testId="germany-grunderwerbsteuer"
                        />
                        <AutoCalcRow
                          label="Notar- & Gerichtskosten (~1.5%)"
                          computed={roi?.notary_fee}
                          value={form.notary_fee}
                          onChange={(v) => updateField("notary_fee", v)}
                          sym={sym}
                          isOverriding={overrides.notary_fee}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, notary_fee: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, notary_fee: false }))
                          }
                          testId="germany-notary-fee"
                        />
                        <AutoCalcRow
                          label="Maklerprovision (3.57% incl. VAT)"
                          computed={roi?.broker_commission}
                          value={form.broker_commission}
                          onChange={(v) => updateField("broker_commission", v)}
                          sym={sym}
                          isOverriding={overrides.broker_commission}
                          onStartOverride={() =>
                            setOverrides((o) => ({ ...o, broker_commission: true }))
                          }
                          onResetOverride={() =>
                            setOverrides((o) => ({ ...o, broker_commission: false }))
                          }
                          testId="germany-broker"
                        />
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">Renovierung & Sanierung</span>
                          <div className="flex items-center gap-2">
                            <input
                              data-testid="germany-renovation"
                              type="number"
                              step="1"
                              value={form.renovation_cost}
                              onChange={(e) =>
                                updateField("renovation_cost", e.target.value)
                              }
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                              placeholder="0"
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white/80">Other Purchase Costs</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="1"
                              value={form.other_purchase_costs}
                              onChange={(e) =>
                                updateField("other_purchase_costs", e.target.value)
                              }
                              className="w-24 sm:w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                              placeholder="0"
                            />
                            <span className="text-white/60 text-[9px]">{sym}</span>
                          </div>
                        </div>
                        {roi && (
                          <div className="flex justify-between text-sm pt-3 border-t border-[#D4AF37]/20">
                            <span className="text-white font-medium">Total Investment</span>
                            <span
                              className="font-mono text-[#D4AF37] font-medium"
                              data-testid="germany-total-investment"
                            >
                              {sym} {roi.total_investment?.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================================ */}
                {/* STEP 3 · INCOME & OPERATIONS                                 */}
                {/* ============================================================ */}
                {/* ============================================================ */}
                {/* STEP 3 · OPERATIONS                                          */}
                {/* ============================================================ */}
                {currentStep === 3 && (
                  <div data-testid="step-3-content">
                    {/* ---------------------------------------------------- */}
                    {/* SECTION 1 — RENTAL INCOME                            */}
                    {/* ---------------------------------------------------- */}
                    <div className="pb-5">
                      <div className="flex items-center justify-between mb-3.5 flex-wrap gap-2">
                        <SectionLabel testId="ops-section-rental">Rental Income</SectionLabel>
                        {/* LTR / STR mode toggle */}
                        <div className="flex items-center gap-1 bg-[#050505] p-0.5">
                          <button
                            type="button"
                            data-testid="asset-income-mode-long"
                            onClick={() => updateField("income_mode", "long_term")}
                            className={`px-2.5 py-1 text-[10px] tracking-wider uppercase transition-colors ${
                              form.income_mode !== "short_term"
                                ? "bg-[#D4AF37] text-black"
                                : "text-white/60 hover:text-white"
                            }`}
                          >
                            Long-term
                          </button>
                          <button
                            type="button"
                            data-testid="asset-income-mode-short"
                            onClick={() => updateField("income_mode", "short_term")}
                            className={`px-2.5 py-1 text-[10px] tracking-wider uppercase transition-colors ${
                              form.income_mode === "short_term"
                                ? "bg-[#D4AF37] text-black"
                                : "text-white/60 hover:text-white"
                            }`}
                          >
                            Short-term (STR)
                          </button>
                        </div>
                      </div>

                      {form.income_mode === "short_term" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className={labelCls}>Nightly rate (avg) ({sym})</label>
                            <input
                              data-testid="asset-nightly-rate"
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.nightly_rate}
                              onChange={(e) => updateField("nightly_rate", e.target.value)}
                              className={monoInputCls}
                              placeholder="e.g. 800"
                            />
                          </div>
                          <div>
                            <label className={labelCls}>Occupancy: {form.occupancy_pct}%</label>
                            <input
                              data-testid="asset-occupancy-pct"
                              type="range"
                              min={0}
                              max={100}
                              step={1}
                              value={form.occupancy_pct}
                              onChange={(e) =>
                                updateField("occupancy_pct", parseFloat(e.target.value))
                              }
                              className="w-full accent-[#D4AF37] mt-2"
                            />
                          </div>
                          <div>
                            <label className={labelCls}>Utilities / yr ({sym})</label>
                            <input
                              data-testid="asset-utilities-annual"
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.utilities_annual}
                              onChange={(e) => updateField("utilities_annual", e.target.value)}
                              className={monoInputCls}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className={labelCls}>Cleaning / mo ({sym})</label>
                            <input
                              data-testid="asset-cleaning-monthly"
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.cleaning_monthly}
                              onChange={(e) => updateField("cleaning_monthly", e.target.value)}
                              className={monoInputCls}
                              placeholder="0"
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className={labelCls}>
                            Monthly Rental Income ({sym}){" "}
                            <span className="text-white/65 normal-case">· optional</span>
                          </label>
                          {deferred.monthly_rental_income ? (
                            <DeferredPill
                              label="Set rental income later"
                              onAddNow={() =>
                                setDeferred((d) => ({ ...d, monthly_rental_income: false }))
                              }
                              testId="rent"
                            />
                          ) : (
                            <>
                              <input
                                data-testid="asset-rental-input"
                                type="number"
                                step="0.01"
                                value={form.monthly_rental_income}
                                onChange={(e) =>
                                  updateField("monthly_rental_income", e.target.value)
                                }
                                className={monoInputCls}
                                placeholder={`e.g. ${ph.rent}`}
                              />
                              <button
                                type="button"
                                data-testid="rent-defer-btn"
                                onClick={() => {
                                  updateField("monthly_rental_income", "");
                                  setDeferred((d) => ({ ...d, monthly_rental_income: true }));
                                }}
                                className={skipLinkCls}
                              >
                                Skip · estimate later
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ---------------------------------------------------- */}
                    {/* SECTION 2 — RUNNING COSTS                            */}
                    {/* ---------------------------------------------------- */}
                    <div
                      className="pt-5 pb-5"
                      style={{ borderTop: "0.5px solid #1e1e1e" }}
                    >
                      <SectionLabel testId="ops-section-running">Running Costs</SectionLabel>

                      {/* Dubai: Service fee + Maintenance per sqft */}
                      {isDubai && (
                        <>
                          <InlineRow label={`Service Fee / SqFt p.a. (${sym})`}>
                            <input
                              data-testid="asset-svc-sqft"
                              type="number"
                              step="0.01"
                              value={form.service_fee_per_sqft}
                              onChange={(e) =>
                                updateField("service_fee_per_sqft", e.target.value)
                              }
                              className={inlineInputCls}
                              style={inlineInputStyle}
                              placeholder={ph.service_fee_per_sqft}
                            />
                          </InlineRow>
                          <InlineRow label={`Maintenance / SqFt p.a. (${sym})`} isLast>
                            <input
                              data-testid="asset-maint-sqft"
                              type="number"
                              step="0.01"
                              value={form.maintenance_per_sqft}
                              onChange={(e) =>
                                updateField("maintenance_per_sqft", e.target.value)
                              }
                              className={inlineInputCls}
                              style={inlineInputStyle}
                              placeholder={ph.maintenance_per_sqft}
                            />
                          </InlineRow>
                        </>
                      )}

                      {/* Cyprus: Site fees + Property tax */}
                      {isCyprus && (
                        <>
                          <InlineRow label={`Site Fees / m² p.a. (${sym})`}>
                            <input
                              data-testid="cyprus-site-fee"
                              type="number"
                              step="0.01"
                              value={form.maintenance_per_sqft}
                              onChange={(e) =>
                                updateField("maintenance_per_sqft", e.target.value)
                              }
                              className={inlineInputCls}
                              style={inlineInputStyle}
                              placeholder={ph.site_fee_per_sqm}
                            />
                          </InlineRow>
                          <InlineRow label={`Property Tax p.a. (${sym})`} isLast>
                            <input
                              data-testid="cyprus-property-tax"
                              type="number"
                              step="0.01"
                              value={form.property_tax_annual}
                              onChange={(e) =>
                                updateField("property_tax_annual", e.target.value)
                              }
                              className={inlineInputCls}
                              style={inlineInputStyle}
                              placeholder={ph.property_tax_annual}
                            />
                          </InlineRow>
                        </>
                      )}

                      {/* Germany: Hausgeld split + Rücklage */}
                      {isGermany && (
                        <>
                          {deferred.hausgeld ? (
                            <DeferredPill
                              label="Hausgeld details · set later"
                              onAddNow={() =>
                                setDeferred((d) => ({ ...d, hausgeld: false }))
                              }
                              testId="germany-hausgeld"
                            />
                          ) : (
                            <>
                              <InlineRow
                                label={`Hausgeld umlagefähig / mo (${sym})`}
                                hint="Recoverable · tenant pays"
                              >
                                <input
                                  data-testid="germany-hausgeld-umlage"
                                  type="number"
                                  step="0.01"
                                  value={form.hausgeld_umlage_monthly}
                                  onChange={(e) =>
                                    updateField("hausgeld_umlage_monthly", e.target.value)
                                  }
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder={ph.hausgeld_umlage}
                                />
                              </InlineRow>
                              <InlineRow
                                label={`Hausgeld nicht-umlagefähig / mo (${sym})`}
                                hint="Non-recoverable · landlord pays"
                              >
                                <input
                                  data-testid="germany-hausgeld-nicht-umlage"
                                  type="number"
                                  step="0.01"
                                  value={form.hausgeld_nicht_umlage_monthly}
                                  onChange={(e) =>
                                    updateField(
                                      "hausgeld_nicht_umlage_monthly",
                                      e.target.value
                                    )
                                  }
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder={ph.hausgeld_nicht_umlage}
                                />
                              </InlineRow>
                              <InlineRow
                                label={`Instandhaltungsrücklage / mo (${sym})`}
                                hint="Maintenance reserve"
                                isLast
                              >
                                <input
                                  data-testid="germany-ruecklage"
                                  type="number"
                                  step="0.01"
                                  value={form.instandhaltungsruecklage_monthly}
                                  onChange={(e) =>
                                    updateField(
                                      "instandhaltungsruecklage_monthly",
                                      e.target.value
                                    )
                                  }
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder={ph.ruecklage}
                                />
                              </InlineRow>
                              <button
                                type="button"
                                data-testid="germany-hausgeld-defer-btn"
                                onClick={() => {
                                  updateField("hausgeld_umlage_monthly", "");
                                  updateField("hausgeld_nicht_umlage_monthly", "");
                                  updateField("instandhaltungsruecklage_monthly", "");
                                  setDeferred((d) => ({ ...d, hausgeld: true }));
                                }}
                                className={skipLinkCls}
                              >
                                Skip · estimate Hausgeld later
                              </button>
                            </>
                          )}
                        </>
                      )}

                      {/* Fallback: location with no jurisdiction-specific rows */}
                      {!isDubai && !isCyprus && !isGermany && (
                        <InlineRow label={`Annual Service Charges (${sym})`} isLast>
                          <input
                            type="number"
                            step="0.01"
                            value={form.service_charges_annual}
                            onChange={(e) =>
                              updateField("service_charges_annual", e.target.value)
                            }
                            className={inlineInputCls}
                            style={inlineInputStyle}
                            placeholder="Annual total"
                          />
                        </InlineRow>
                      )}
                    </div>

                    {/* ---------------------------------------------------- */}
                    {/* SECTION 3 — ADVANCED ASSUMPTIONS  (collapsed by def) */}
                    {/* ---------------------------------------------------- */}
                    <div>
                      <button
                        type="button"
                        data-testid="additional-costs-toggle"
                        onClick={() => setShowAdditional((v) => !v)}
                        className="w-full flex items-center justify-between transition-colors"
                        style={{
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          color: "#555",
                          borderTop: "0.5px solid #191919",
                          padding: "8px 0",
                          cursor: "pointer",
                        }}
                      >
                        <span className="flex items-center gap-2">
                          <ChevronRight
                            className="w-3.5 h-3.5 transition-transform"
                            style={{
                              transform: showAdditional ? "rotate(90deg)" : "rotate(0deg)",
                            }}
                          />
                          Advanced assumptions
                          <span
                            style={{
                              textTransform: "none",
                              letterSpacing: "0",
                              color: "#3a3a3a",
                              fontSize: "11px",
                            }}
                          >
                            (reserves, management, insurance)
                          </span>
                        </span>
                      </button>

                      <AnimatePresence initial={false}>
                        {showAdditional && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-3 pb-1">
                              <InlineRow label="Vacancy Reserve (%)">
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={form.vacancy_reserve_pct}
                                  onChange={(e) =>
                                    updateField("vacancy_reserve_pct", e.target.value)
                                  }
                                  data-testid="asset-vacancy-reserve-pct"
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder="2"
                                />
                              </InlineRow>
                              <InlineRow label="Maintenance Reserve (% of price)">
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={form.maintenance_reserve_pct}
                                  onChange={(e) =>
                                    updateField(
                                      "maintenance_reserve_pct",
                                      e.target.value
                                    )
                                  }
                                  data-testid="asset-maintenance-reserve-pct"
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder="1"
                                />
                              </InlineRow>
                              <InlineRow label="Management Fee (% of rent)">
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={form.management_fee_pct}
                                  onChange={(e) =>
                                    updateField("management_fee_pct", e.target.value)
                                  }
                                  data-testid="asset-management-fee-pct"
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder="10"
                                />
                              </InlineRow>
                              <InlineRow label={`Insurance / yr (${sym})`}>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={form.insurance_annual}
                                  onChange={(e) =>
                                    updateField("insurance_annual", e.target.value)
                                  }
                                  data-testid="asset-insurance-annual"
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder="0"
                                />
                              </InlineRow>
                              <InlineRow label={`Other Annual Costs (${sym})`} isLast>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={
                                    isDubai || isCyprus || isGermany
                                      ? form.other_annual_costs
                                      : form.service_charges_annual
                                  }
                                  onChange={(e) =>
                                    updateField(
                                      isDubai || isCyprus || isGermany
                                        ? "other_annual_costs"
                                        : "service_charges_annual",
                                      e.target.value
                                    )
                                  }
                                  className={inlineInputCls}
                                  style={inlineInputStyle}
                                  placeholder="Optional"
                                />
                              </InlineRow>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* ============================================================ */}
                {/* STEP 4 · FINANCING                                           */}
                {/* ============================================================ */}
                {currentStep === 4 && (
                  <div data-testid="step-4-content">
                    {/* Bank Financing toggle row */}
                    <div className="flex items-center justify-between py-1">
                      <span style={{ fontSize: "13px", color: "#FFFFFF" }}>
                        Bank Financing
                      </span>
                      <button
                        type="button"
                        onClick={() => updateField("is_financed", !form.is_financed)}
                        data-testid="asset-financing-toggle"
                        className={`w-11 h-6 rounded-full transition-colors relative ${
                          form.is_financed ? "bg-[#D4AF37]" : "bg-white/10"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                            form.is_financed ? "left-[22px]" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>

                    {!form.is_financed && (
                      <p
                        data-testid="financing-off-hint"
                        style={{
                          fontSize: "12px",
                          color: "#444",
                          marginTop: "12px",
                        }}
                      >
                        No financing — this property is purchased outright.
                      </p>
                    )}

                    {form.is_financed && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="pt-4"
                      >
                        <InlineRow label="Loan Amount">
                          <div className="flex items-center justify-end gap-2">
                            <input
                              data-testid="asset-loan-amount"
                              type="number"
                              step="1"
                              value={form.loan_amount}
                              onChange={(e) =>
                                updateField("loan_amount", e.target.value)
                              }
                              className={inlineInputCls}
                              style={inlineInputStyle}
                              placeholder="0"
                            />
                            <span
                              className="shrink-0"
                              style={{
                                fontSize: "11px",
                                color: "#555",
                                marginLeft: "8px",
                              }}
                            >
                              {sym}
                            </span>
                          </div>
                        </InlineRow>
                        <InlineRow label="Interest Rate">
                          <div className="flex items-center justify-end gap-2">
                            <input
                              data-testid="asset-interest-rate"
                              type="number"
                              step="0.01"
                              value={form.interest_rate_pct}
                              onChange={(e) =>
                                updateField("interest_rate_pct", e.target.value)
                              }
                              className={inlineInputCls}
                              style={inlineInputStyle}
                              placeholder="0.00"
                            />
                            <span
                              className="shrink-0"
                              style={{
                                fontSize: "11px",
                                color: "#555",
                                marginLeft: "8px",
                              }}
                            >
                              %
                            </span>
                          </div>
                        </InlineRow>
                        <InlineRow label="Amortization Rate">
                          <div className="flex items-center justify-end gap-2">
                            <input
                              data-testid="asset-amortization"
                              type="number"
                              step="0.01"
                              value={form.amortization_pct}
                              onChange={(e) =>
                                updateField("amortization_pct", e.target.value)
                              }
                              disabled={form.amortization_type === "interest_only"}
                              className={`${inlineInputCls} disabled:opacity-40`}
                              style={inlineInputStyle}
                              placeholder="0.00"
                            />
                            <span
                              className="shrink-0"
                              style={{
                                fontSize: "11px",
                                color: "#555",
                                marginLeft: "8px",
                              }}
                            >
                              %
                            </span>
                          </div>
                        </InlineRow>

                        <div className="pt-4">
                          <label
                            style={{
                              fontSize: "13px",
                              color: "#FFFFFF",
                              display: "block",
                              marginBottom: "6px",
                            }}
                          >
                            Amortization Type
                          </label>
                          <select
                            data-testid="asset-amortization-type"
                            value={form.amortization_type}
                            onChange={(e) =>
                              updateField("amortization_type", e.target.value)
                            }
                            className={monoInputCls}
                          >
                            <option value="amortizing">Amortizing</option>
                            <option value="interest_only">Interest-only</option>
                          </select>
                        </div>

                        {/* Live derived figures (unchanged math) */}
                        {roi?.is_financed && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="space-y-1.5 pt-5 text-xs"
                          >
                            <div className="flex justify-between">
                              <span className="text-white/60">Downpayment</span>
                              <span className="font-mono text-white">
                                {sym} {roi.downpayment?.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/60">Equity Invested</span>
                              <span className="font-mono text-[#D4AF37]">
                                {sym} {roi.equity_invested?.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/60">Annual Interest</span>
                              <span className="font-mono text-red-400/70">
                                -{sym} {roi.annual_interest?.toLocaleString()}
                              </span>
                            </div>
                            {roi.annual_amortization > 0 && (
                              <div className="flex justify-between">
                                <span className="text-white/60">Annual Amortization</span>
                                <span className="font-mono text-red-400/70">
                                  -{sym} {roi.annual_amortization?.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </div>
                )}

                {/* ============================================================ */}
                {/* STEP 5 · REVIEW                                              */}
                {/* ============================================================ */}
                {currentStep === 5 && (
                  <div className="space-y-4" data-testid="step-5-content">
                    {/* Property */}
                    <div className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37]">
                          Property
                        </p>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(1)}
                          className="text-[10px] tracking-[0.12em] uppercase text-white/55 hover:text-[#D4AF37] transition-colors"
                          data-testid="review-edit-property"
                        >
                          Edit
                        </button>
                      </div>
                      <ReviewRow k="Name" v={form.name || "—"} />
                      <ReviewRow k="Location" v={locationLabel} />
                      <ReviewRow
                        k="Type"
                        v={
                          form.property_type === "off_plan"
                            ? "Off Plan"
                            : form.property_type.charAt(0).toUpperCase() +
                              form.property_type.slice(1)
                        }
                      />
                      <ReviewRow
                        k="Purchase Date"
                        v={
                          form.purchase_date
                            ? format(
                                new Date(form.purchase_date + "T00:00:00"),
                                "MMM dd, yyyy"
                              )
                            : "—"
                        }
                      />
                      <ReviewRow
                        k="Purchase Price"
                        v={fmtMoney(form.purchase_price)}
                        emphasised
                      />
                      <ReviewRow
                        k={`Area (${areaUnit})`}
                        v={form.area_sqft || "—"}
                      />
                    </div>

                    {/* Acquisition */}
                    <div className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37]">
                          Acquisition
                        </p>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(2)}
                          className="text-[10px] tracking-[0.12em] uppercase text-white/55 hover:text-[#D4AF37] transition-colors"
                          data-testid="review-edit-acquisition"
                        >
                          Edit
                        </button>
                      </div>
                      {isDubai && (
                        <>
                          <ReviewRow k="DLD Transfer Fee" v={fmtMoney(roi?.dld_fee)} />
                          <ReviewRow
                            k="Brokerage Fees"
                            v={fmtMoney(roi?.broker_commission)}
                          />
                          <ReviewRow k="Trustee Fee" v={fmtMoney(roi?.trustee_fee)} />
                          {parseFloat(form.furniture_cost) > 0 && (
                            <ReviewRow k="Furniture" v={fmtMoney(form.furniture_cost)} />
                          )}
                        </>
                      )}
                      {isCyprus && (
                        <>
                          <ReviewRow
                            k="Build Status"
                            v={form.is_new_build ? "New Build" : "Resale"}
                          />
                          <ReviewRow k="Stamp Duty" v={fmtMoney(roi?.stamp_duty)} />
                          {form.is_new_build && (
                            <ReviewRow k="VAT" v={fmtMoney(roi?.vat)} />
                          )}
                          <ReviewRow
                            k="Title Deed Transfer"
                            v={fmtMoney(roi?.transfer_fee)}
                          />
                          {parseFloat(form.solicitor_fee) > 0 && (
                            <ReviewRow
                              k="Solicitor"
                              v={fmtMoney(form.solicitor_fee)}
                            />
                          )}
                        </>
                      )}
                      {isGermany && (
                        <>
                          <ReviewRow
                            k={`Grunderwerbsteuer (${
                              roi?.grunderwerbsteuer_rate_pct || "—"
                            }%)`}
                            v={fmtMoney(roi?.grunderwerbsteuer)}
                          />
                          <ReviewRow k="Notar- & Gerichtskosten" v={fmtMoney(roi?.notary_fee)} />
                          <ReviewRow
                            k="Maklerprovision"
                            v={fmtMoney(roi?.broker_commission)}
                          />
                          {parseFloat(form.renovation_cost) > 0 && (
                            <ReviewRow
                              k="Renovierung"
                              v={fmtMoney(form.renovation_cost)}
                            />
                          )}
                        </>
                      )}
                      {roi?.total_investment != null && (
                        <ReviewRow
                          k="Total Investment"
                          v={fmtMoney(roi.total_investment)}
                          emphasised
                        />
                      )}
                    </div>

                    {/* Income */}
                    <div className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37]">
                          Operations
                        </p>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(3)}
                          className="text-[10px] tracking-[0.12em] uppercase text-white/55 hover:text-[#D4AF37] transition-colors"
                          data-testid="review-edit-income"
                        >
                          Edit
                        </button>
                      </div>
                      <ReviewRow
                        k="Mode"
                        v={
                          form.income_mode === "short_term"
                            ? "Short-term (STR)"
                            : "Long-term"
                        }
                      />
                      {form.income_mode === "short_term" ? (
                        <>
                          <ReviewRow
                            k="Nightly Rate"
                            v={fmtMoney(form.nightly_rate)}
                          />
                          <ReviewRow k="Occupancy" v={`${form.occupancy_pct}%`} />
                        </>
                      ) : (
                        <ReviewRow
                          k="Monthly Rent"
                          v={
                            form.monthly_rental_income
                              ? fmtMoney(form.monthly_rental_income)
                              : "Set later"
                          }
                        />
                      )}
                      <ReviewRow
                        k="Financing"
                        v={form.is_financed ? "On" : "Off"}
                      />
                      {form.is_financed && (
                        <>
                          <ReviewRow
                            k="Loan Amount"
                            v={fmtMoney(form.loan_amount)}
                          />
                          <ReviewRow
                            k="Interest"
                            v={`${form.interest_rate_pct || "—"}%`}
                          />
                        </>
                      )}
                      {roi && parseFloat(form.monthly_rental_income) > 0 && (
                        <>
                          <ReviewRow
                            k="Monthly Net"
                            v={fmtMoney(roi.monthly_cashflow)}
                          />
                          <ReviewRow
                            k="Equity Yield"
                            v={
                              roi.equity_yield_pct == null
                                ? "—"
                                : `${roi.equity_yield_pct}%`
                            }
                            emphasised
                          />
                          <ReviewRow
                            k="Gross ROI"
                            v={`${roi.total_roi_pct}%`}
                            emphasised
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* ============================================================ */}
                {/* FOOTER · Prev / Next / Submit                                */}
                {/* ============================================================ */}
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/[0.10]">
                  <button
                    type="button"
                    onClick={goBack}
                    disabled={currentStep === 1}
                    data-testid="wizard-back-btn"
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-white/65 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Back
                  </button>

                  {currentStep < 5 ? (
                    <button
                      type="button"
                      onClick={goNext}
                      data-testid="wizard-next-btn"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] tracking-[0.15em] uppercase transition-colors"
                    >
                      Continue
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      data-testid="submit-asset-btn"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black text-[11px] tracking-[0.15em] uppercase font-medium hover:bg-[#B5952F] transition-colors disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                      {loading ? "Saving…" : isEditMode ? "Save Changes" : "Add to Portfolio"}
                    </button>
                  )}
                </div>
              </form>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Small read-only row used inside the Step 4 review summary.
function ReviewRow({ k, v, emphasised }) {
  return (
    <div className="flex justify-between items-baseline text-xs gap-3">
      <span className="text-white/65">{k}</span>
      <span
        className={`font-mono text-right ${
          emphasised ? "text-[#D4AF37] font-medium" : "text-white"
        }`}
      >
        {v}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CountryPicker: premium market-selection gate shown only when the parent
// opts in via `enableCountryPicker`. The three fully-supported markets route
// straight into the original wizard (chooseCountry → view "wizard"); their
// workflows and calculations are untouched. "Somewhere Else" is a Beta entry
// to simplified worldwide tracking.
// ---------------------------------------------------------------------------
const SUPPORTED_MARKETS = [
  {
    id: "dubai",
    label: "Dubai / UAE",
    meta: "AED · Full support",
    desc: "DLD transfer, trustee and brokerage fees calculated automatically.",
  },
  {
    id: "north_cyprus",
    label: "Northern Cyprus",
    meta: "GBP · Full support",
    desc: "Stamp duty, VAT and title-deed transfer handled for you.",
  },
  {
    id: "germany",
    label: "Germany",
    meta: "EUR · Full support",
    desc: "Grunderwerbsteuer, Notar and Hausgeld computed by Bundesland.",
  },
];

function CountryPicker({ onChoose, onChooseOther }) {
  return (
    <div className="p-5" data-testid="country-picker">
      <p className="text-white/70 text-sm mb-5 leading-relaxed">
        Where is the property located? Choose a fully supported market for
        automatic tax &amp; fee calculations, or track a property anywhere else
        in the world.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SUPPORTED_MARKETS.map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`country-card-${m.id}`}
            onClick={() => onChoose(m.id)}
            className="group text-left bg-[#050505] border border-white/[0.10] hover:border-[#D4AF37]/60 p-4 transition-colors focus:outline-none focus:border-[#D4AF37]"
          >
            <span className="font-serif text-base text-white group-hover:text-[#F5F0E8] block">
              {m.label}
            </span>
            <span className="block text-[9px] tracking-[0.15em] uppercase text-[#D4AF37]/80 mt-1">
              {m.meta}
            </span>
            <span className="block text-[11px] text-white/50 mt-2 leading-snug">
              {m.desc}
            </span>
          </button>
        ))}

        {/* Somewhere Else — Beta */}
        <button
          type="button"
          data-testid="country-card-other"
          onClick={onChooseOther}
          className="group relative text-left bg-[#050505] border border-[#D4AF37]/25 hover:border-[#D4AF37]/70 p-4 transition-colors focus:outline-none focus:border-[#D4AF37]"
        >
          <span
            data-testid="country-other-beta-badge"
            className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 border border-[#D4AF37]/50 text-[#D4AF37] text-[8px] tracking-[0.2em] uppercase bg-[#D4AF37]/10 rounded-sm"
          >
            Beta
          </span>
          <span className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-serif text-base text-white group-hover:text-[#F5F0E8]">
              Somewhere Else
            </span>
          </span>
          <span className="block text-[11px] text-white/55 mt-2 leading-snug pr-10">
            Own property somewhere else in the world? Add it to your global
            portfolio using our simplified property tracking.
          </span>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleGlobalForm: simplified worldwide property registration ("Somewhere
// Else" — Beta). Persists into the SAME asset collection with location
// "other" and registration_mode "simplified_global". NO country-specific tax
// / fee engine runs for these assets (backend `else` branch → total_investment
// = purchase_price; no invented amounts). Handles both create and edit.
// ---------------------------------------------------------------------------
const PROPERTY_STATUSES = [
  { id: "off_plan", label: "Off-Plan" },
  { id: "under_construction", label: "Under Construction" },
  { id: "completed", label: "Completed" },
];

const GLOBAL_PROPERTY_TYPES = [
  { v: "", label: "Not specified" },
  { v: "apartment", label: "Apartment" },
  { v: "house", label: "House" },
  { v: "villa", label: "Villa" },
  { v: "land", label: "Land / Plot" },
  { v: "commercial", label: "Commercial" },
  { v: "other", label: "Other" },
];

const calClassNames = {
  caption_label: "hidden",
  caption_dropdowns: "flex gap-2 items-center",
  dropdown:
    "bg-[#0A0A0A] text-white text-sm border border-white/10 hover:border-[#D4AF37]/40 px-2 py-1 outline-none cursor-pointer",
  dropdown_month: "relative",
  dropdown_year: "relative",
  vhidden: "hidden",
};
const calClassName =
  "[&_.rdp-day_selected]:bg-[#D4AF37] [&_.rdp-day_selected]:text-black [&_.rdp-head_cell]:text-white/65 [&_.rdp-caption]:text-white [&_.rdp-nav_button]:text-white/60 [&_.rdp-nav_button:hover]:text-white [&_.rdp-cell]:text-white/85 [&_.rdp-day:hover]:bg-white/10 [&_.rdp-day_today]:border [&_.rdp-day_today]:border-[#D4AF37]/40";

function SimpleGlobalForm({ asset, onBack, onClose, onSuccess }) {
  const isEdit = !!asset;
  const [form, setForm] = useState(() => ({
    name: asset?.name || "",
    country_code: asset?.country_code || "",
    country_name: asset?.country_name || "",
    purchase_price: asset?.purchase_price != null ? String(asset.purchase_price) : "",
    currency: asset?.currency || "",
    purchase_date: asset?.purchase_date || "",
    property_status: asset?.property_status || "",
    city: asset?.city || "",
    address: asset?.address || "",
    property_type:
      asset?.property_type && GLOBAL_PROPERTY_TYPES.some((t) => t.v === asset.property_type)
        ? asset.property_type
        : "",
    ownership_pct: asset?.ownership_pct != null ? String(asset.ownership_pct) : "",
    notes: asset?.description || "",
    estimated_market_value:
      asset?.estimated_market_value != null ? String(asset.estimated_market_value) : "",
    market_valuation_date: asset?.market_valuation_date || "",
    valuation_source: asset?.valuation_source || "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [valDateOpen, setValDateOpen] = useState(false);

  // ── Optional financial tracking (simplified global only) ──────────────────
  const [payments, setPayments] = useState(() =>
    (asset?.payment_milestones || []).map((m) => ({
      id: m.id || `${Math.random().toString(36).slice(2)}`,
      name: m.name || m.description || "",
      amount:
        m.original_amount != null ? String(m.original_amount)
        : m.amount != null ? String(m.amount) : "",
      currency: m.currency || asset?.currency || "",
      due_date: m.due_date || m.date || "",
      // Prefer the authoritative is_paid flag over a possibly-stale `status`
      // (the detail-modal "Mark paid" toggle updates is_paid but not status).
      status: m.is_paid ? "paid" : m.status === "overdue" ? "overdue" : "pending",
      paid_date: m.paid_date || "",
      exchange_rate: m.exchange_rate != null ? String(m.exchange_rate) : "",
    }))
  );
  const [customCosts, setCustomCosts] = useState(() =>
    (asset?.custom_costs || []).map((c) => ({
      id: c.id || `${Math.random().toString(36).slice(2)}`,
      type: c.type || "other",
      label: c.label || "",
      amount: c.amount != null ? String(c.amount) : "",
      currency: c.currency || asset?.currency || "",
      date: c.date || "",
    }))
  );
  const [rental, setRental] = useState(() => {
    const rt = asset?.rental_tracking || {};
    const toStrMap = (o = {}) =>
      Object.fromEntries(Object.entries(o).map(([k, v]) => [k, v != null ? String(v) : ""]));
    return { actual: toStrMap(rt.actual), projected: toStrMap(rt.projected) };
  });
  const [openSection, setOpenSection] = useState(null);
  const toggleSection = (s) => setOpenSection((cur) => (cur === s ? null : s));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const purchaseDateObj = form.purchase_date
    ? new Date(form.purchase_date + "T00:00:00")
    : undefined;
  const valDateObj = form.market_valuation_date
    ? new Date(form.market_valuation_date + "T00:00:00")
    : undefined;
  const currencyLabel = (() => {
    const c = CURRENCIES.find((x) => x.code === form.currency);
    return c ? `${c.code} · ${c.name}` : "";
  })();

  const inputCls =
    "w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm placeholder:text-white/40 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none";
  const monoInputCls = `${inputCls} font-mono`;
  const labelCls = "text-[10px] tracking-[0.15em] uppercase text-white mb-1.5 block";
  const triggerCls =
    "w-full bg-[#050505] border border-white/10 text-left px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none flex items-center justify-between hover:border-white/20 transition-colors";
  const itemCls =
    "text-white/85 cursor-pointer data-[selected=true]:!bg-[#D4AF37]/20 data-[selected=true]:!text-white";
  const req = <span className="text-[#D4AF37]">*</span>;

  const validate = () => {
    if (!form.name.trim()) return "Please add a property name.";
    if (!form.country_code) return "Please select a country.";
    if (!form.purchase_price || parseFloat(form.purchase_price) <= 0)
      return "Please add a valid purchase price.";
    if (!form.currency) return "Please select a purchase currency.";
    if (!form.purchase_date) return "Please select a purchase date.";
    if (!form.property_status) return "Please select a property status.";
    const pct = form.ownership_pct !== "" ? parseFloat(form.ownership_pct) : null;
    if (pct != null && (Number.isNaN(pct) || pct <= 0 || pct > 100))
      return "Ownership % must be between 0 and 100.";
    return "";
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const baseCur = form.currency;
      const payment_milestones = serializePayments(payments, baseCur);
      const custom_costs = serializeCustomCosts(customCosts, baseCur);
      const other_purchase_costs = customCostsBaseTotal(customCosts, baseCur);
      const rental_tracking = normalizeRental(rental);
      const { monthly_rental_income, other_annual_costs } = rentalEngineInputs(rental);
      const payload = {
        name: form.name.trim(),
        location: "other",
        registration_mode: "simplified_global",
        country_code: form.country_code,
        country_name: form.country_name,
        currency: form.currency,
        purchase_price: parseFloat(form.purchase_price),
        purchase_date: form.purchase_date,
        property_status: form.property_status,
        property_type: form.property_type || "residential",
        city: form.city.trim() || null,
        address: form.address.trim() || null,
        ownership_pct: form.ownership_pct !== "" ? parseFloat(form.ownership_pct) : null,
        description: form.notes.trim(),
        estimated_market_value:
          form.estimated_market_value !== "" ? parseFloat(form.estimated_market_value) : null,
        market_valuation_date: form.market_valuation_date || null,
        valuation_source: form.valuation_source || null,
        // Optional financial tracking (A/B/C). Derived engine inputs keep the
        // existing "other" ROI path honest — no country-specific fees invented.
        payment_milestones,
        custom_costs,
        other_purchase_costs,
        rental_tracking,
        monthly_rental_income,
        other_annual_costs,
      };
      if (isEdit) {
        await api.put(`/assets/${asset.id}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/assets", payload);
        toast.success("Property added to your global portfolio");
      }
      onSuccess?.();
      onClose?.();
    } catch (err2) {
      const d = err2.response?.data?.detail;
      setError(typeof d === "string" ? d : "Failed to save property. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5" data-testid="simple-global-form">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          data-testid="other-flow-back-btn"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-white/65 hover:text-white transition-colors mb-5"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to markets
        </button>
      )}

      {/* Gold-bordered Beta notice */}
      <div
        data-testid="beta-notice-panel"
        className="border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] p-5 rounded-sm mb-6"
      >
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif text-base text-[#F5F0E8]">
                Simplified Property Tracking
              </h3>
              <span className="inline-flex items-center px-2 py-0.5 border border-[#D4AF37]/50 text-[#D4AF37] text-[8px] tracking-[0.2em] uppercase bg-[#D4AF37]/10 rounded-sm">
                Beta
              </span>
            </div>
            <p className="text-[12.5px] text-white/70 leading-relaxed mt-3">
              Properties outside our fully supported markets can be added using a
              simplified tracking system. Country-specific taxes, legal
              requirements, and acquisition fees are not automatically
              calculated. Please enter your actual costs manually and verify them
              with a qualified local professional. Some advanced financial
              features may not be available yet.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 text-sm"
          data-testid="simple-global-error"
        >
          {error}
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()} noValidate className="space-y-5">
        <SectionLabel testId="simple-essentials-label">Property Essentials</SectionLabel>

        {/* Property Name */}
        <div>
          <label className={labelCls}>Property Name {req}</label>
          <input
            data-testid="global-name-input"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
            placeholder="e.g. Lakeview Residence · Unit 7"
            maxLength={120}
          />
        </div>

        {/* Country */}
        <div>
          <label className={labelCls}>Country {req}</label>
          <Popover open={countryOpen} onOpenChange={setCountryOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid="global-country-trigger" className={triggerCls}>
                <span className={form.country_name ? "text-white" : "text-white/40"}>
                  {form.country_name || "Search & select a country"}
                </span>
                <ChevronDown className="w-4 h-4 text-white/65 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="p-0 bg-[#0A0A0A] border border-white/10"
              style={{ width: "var(--radix-popover-trigger-width)" }}
            >
              <Command className="bg-[#0A0A0A]">
                <CommandInput
                  placeholder="Type a country…"
                  data-testid="global-country-search"
                  className="text-white placeholder:text-white/40"
                />
                <CommandList>
                  <CommandEmpty>No country found.</CommandEmpty>
                  <CommandGroup>
                    {COUNTRIES.map((c) => (
                      <CommandItem
                        key={c.code}
                        value={c.name}
                        data-testid={`global-country-option-${c.code}`}
                        onSelect={() => {
                          setForm((f) => ({ ...f, country_code: c.code, country_name: c.name }));
                          setCountryOpen(false);
                        }}
                        className={itemCls}
                      >
                        <span>{c.name}</span>
                        {form.country_code === c.code && (
                          <Check className="ml-auto w-3.5 h-3.5 text-[#D4AF37]" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Purchase Price + Currency */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Purchase Price {req}</label>
            <input
              data-testid="global-price-input"
              type="number"
              step="0.01"
              min="0"
              value={form.purchase_price}
              onChange={(e) => set("purchase_price", e.target.value)}
              className={monoInputCls}
              placeholder="e.g. 250000"
            />
          </div>
          <div>
            <label className={labelCls}>Purchase Currency {req}</label>
            <Popover open={currencyOpen} onOpenChange={setCurrencyOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="global-currency-trigger"
                  className={triggerCls}
                >
                  <span className={form.currency ? "text-white" : "text-white/40"}>
                    {currencyLabel || "Select currency"}
                  </span>
                  <ChevronDown className="w-4 h-4 text-white/65 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="p-0 bg-[#0A0A0A] border border-white/10"
                style={{ width: "var(--radix-popover-trigger-width)" }}
              >
                <Command className="bg-[#0A0A0A]">
                  <CommandInput
                    placeholder="Search currency…"
                    data-testid="global-currency-search"
                    className="text-white placeholder:text-white/40"
                  />
                  <CommandList>
                    <CommandEmpty>No currency found.</CommandEmpty>
                    <CommandGroup>
                      {CURRENCIES.map((c) => (
                        <CommandItem
                          key={c.code}
                          value={`${c.code} ${c.name}`}
                          data-testid={`global-currency-option-${c.code}`}
                          onSelect={() => {
                            set("currency", c.code);
                            setCurrencyOpen(false);
                          }}
                          className={itemCls}
                        >
                          <span className="font-mono text-[#D4AF37] mr-2">{c.code}</span>
                          <span className="text-white/70">{c.name}</span>
                          {form.currency === c.code && (
                            <Check className="ml-auto w-3.5 h-3.5 text-[#D4AF37]" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-[10px] text-white/40 mt-1.5">
              Choose the currency you actually paid in — it is never guessed from the country.
            </p>
          </div>
        </div>

        {/* Purchase Date */}
        <div>
          <label className={labelCls}>Purchase Date {req}</label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="global-date-picker-btn"
                className={triggerCls}
              >
                <span className={form.purchase_date ? "text-white" : "text-white/40"}>
                  {form.purchase_date
                    ? format(new Date(form.purchase_date + "T00:00:00"), "MMM dd, yyyy")
                    : "Select date"}
                </span>
                <CalendarDays className="w-4 h-4 text-white/65 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 bg-[#0A0A0A] border border-white/10"
              align="start"
            >
              <Calendar
                mode="single"
                selected={purchaseDateObj}
                onSelect={(d) => {
                  if (d) {
                    set("purchase_date", format(d, "yyyy-MM-dd"));
                    setDateOpen(false);
                  }
                }}
                initialFocus
                captionLayout="dropdown-buttons"
                fromYear={new Date().getFullYear() - 60}
                toYear={new Date().getFullYear() + 1}
                defaultMonth={purchaseDateObj || new Date()}
                classNames={calClassNames}
                className={calClassName}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Property Status */}
        <div>
          <label className={labelCls}>Property Status {req}</label>
          <div className="grid grid-cols-3 gap-2" data-testid="global-status-group">
            {PROPERTY_STATUSES.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`global-status-${s.id}`}
                onClick={() => set("property_status", s.id)}
                className={`px-2 py-2.5 text-[11px] tracking-[0.06em] uppercase border transition-colors ${
                  form.property_status === s.id
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-white/10 text-white/60 hover:border-white/25"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-1">
          <SectionLabel testId="simple-optional-label">Optional Details</SectionLabel>
        </div>

        {/* City + Property Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>City</label>
            <input
              data-testid="global-city-input"
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
              className={inputCls}
              placeholder="e.g. Asunción"
            />
          </div>
          <div>
            <label className={labelCls}>Property Type</label>
            <select
              data-testid="global-type-select"
              value={form.property_type}
              onChange={(e) => set("property_type", e.target.value)}
              className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none"
            >
              {GLOBAL_PROPERTY_TYPES.map((t) => (
                <option key={t.v || "none"} value={t.v}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Address */}
        <div>
          <label className={labelCls}>Property Address</label>
          <input
            data-testid="global-address-input"
            value={form.address}
            onChange={(e) => set("address", e.target.value)}
            className={inputCls}
            placeholder="Street, number, district…"
          />
        </div>

        {/* Ownership % */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Ownership %</label>
            <input
              data-testid="global-ownership-input"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.ownership_pct}
              onChange={(e) => set("ownership_pct", e.target.value)}
              className={monoInputCls}
              placeholder="e.g. 100"
            />
          </div>
        </div>

        {/* Market value + valuation date + source */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Current Estimated Market Value</label>
            <input
              data-testid="global-market-value-input"
              type="number"
              step="0.01"
              min="0"
              value={form.estimated_market_value}
              onChange={(e) => set("estimated_market_value", e.target.value)}
              className={monoInputCls}
              placeholder="e.g. 275000"
            />
          </div>
          <div>
            <label className={labelCls}>Market Valuation Date</label>
            <Popover open={valDateOpen} onOpenChange={setValDateOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="global-valuation-date-btn"
                  className={triggerCls}
                >
                  <span className={form.market_valuation_date ? "text-white" : "text-white/40"}>
                    {form.market_valuation_date
                      ? format(
                          new Date(form.market_valuation_date + "T00:00:00"),
                          "MMM dd, yyyy"
                        )
                      : "Select date"}
                  </span>
                  <CalendarDays className="w-4 h-4 text-white/65 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-[#0A0A0A] border border-white/10"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={valDateObj}
                  onSelect={(d) => {
                    if (d) {
                      set("market_valuation_date", format(d, "yyyy-MM-dd"));
                      setValDateOpen(false);
                    }
                  }}
                  initialFocus
                  captionLayout="dropdown-buttons"
                  fromYear={new Date().getFullYear() - 60}
                  toYear={new Date().getFullYear() + 1}
                  defaultMonth={valDateObj || new Date()}
                  classNames={calClassNames}
                  className={calClassName}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className={labelCls}>Valuation Source</label>
            <select
              data-testid="global-valuation-source-select"
              value={form.valuation_source}
              onChange={(e) => set("valuation_source", e.target.value)}
              className={`${inputCls} cursor-pointer`}
            >
              {VALUATION_SOURCES.map((s) => (
                <option key={s.v} value={s.v} className="bg-[#0A0A0A]">{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Optional financial tracking (A/B/C/D) ── */}
        <div className="pt-1">
          <SectionLabel testId="simple-financials-label">Financial Tracking (Optional)</SectionLabel>
        </div>
        <div className="space-y-2.5">
          <PaymentTrackingSection
            open={openSection === "payments"}
            onToggle={() => toggleSection("payments")}
            payments={payments}
            setPayments={setPayments}
            baseCurrency={form.currency}
            purchasePrice={form.purchase_price}
          />
          <CustomCostsSection
            open={openSection === "costs"}
            onToggle={() => toggleSection("costs")}
            costs={customCosts}
            setCosts={setCustomCosts}
            baseCurrency={form.currency}
          />
          <RentalTrackingSection
            open={openSection === "rental"}
            onToggle={() => toggleSection("rental")}
            rental={rental}
            setRental={setRental}
            baseCurrency={form.currency}
          />
          {isEdit && asset?.id ? (
            <GlobalDocumentsSection
              open={openSection === "documents"}
              onToggle={() => toggleSection("documents")}
              assetId={asset.id}
            />
          ) : (
            <div className="border border-dashed border-white/[0.12] px-4 py-3 flex items-center gap-3" data-testid="global-docs-locked">
              <FileText className="w-4 h-4 text-white/30 shrink-0" />
              <p className="text-[11.5px] text-white/40">
                Save this property first, then reopen it to attach contracts and
                documents to your secure Document Vault.
              </p>
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className={labelCls}>Notes</label>
          <textarea
            data-testid="global-notes-input"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={3}
            className={`${inputCls} resize-none`}
            placeholder="Anything worth remembering about this property…"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/[0.10]">
          <button
            type="button"
            onClick={onClose}
            data-testid="simple-global-cancel-btn"
            className="text-[11px] tracking-[0.15em] uppercase text-white/65 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="simple-global-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black text-[11px] tracking-[0.15em] uppercase font-medium hover:bg-[#B5952F] transition-colors disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add to Portfolio"}
          </button>
        </div>
      </form>
    </div>
  );
}
